// Per-trade landing page content. Powers /for/:trade routes. Every
// entry here adds a new page without touching the route file.
//
// Two-tier pain treatment:
//   - painHero: the single most relatable pain for this trade,
//     rendered as a big quoted callout under "Sound familiar?". Has a
//     `problem` half (what the trader actually feels) and a
//     `solution` half (how Manos addresses it). Region-keyed so a
//     UK plumber sees a UK-flavoured pain and a US plumber sees the
//     equivalent. Falls back to first-array entry when no region
//     match.
//   - painPoints: three supporting pains in a 3-col grid below the
//     hero. Plain bullet text -- no quote treatment. Region-keyed
//     under the same scheme as painHero.
//
// painHero replaces what used to be the standalone "testimonials"
// section. The previous TestimonialSlice card felt fake (manufactured
// trader names attributed to real Reddit-paraphrased pains); folding
// the quote into "Sound familiar?" without an attributed name is
// more honest and lands harder -- the trader recognises themselves.
//
// Localisation contract (#129):
//   - Top-level text fields (heroHeadline, heroSub, painPoints,
//     benefits, workflow, mockAppScreen, faqs) are region-keyed via
//     `LocalizedTradeField<T> = Partial<Record<TradeRegion, T>>`.
//   - Every trade ships GB / US / CA explicitly. EU + ROW visitors
//     fall back to GB through `resolveLocalized`.
//   - tapToPayTicketAmount is a per-region number map; the loader
//     resolves it to the visitor's region before formatting with
//     Intl currency.
//   - displayName is an optional per-region override of `name` for
//     trades whose default UK name reads awkwardly elsewhere
//     (e.g. "Painter & decorator" -> "Painter" in US/CA).

export type TradeAccent =
  | "amber"
  | "blue"
  | "green"
  | "rose"
  | "violet"
  | "teal"
  | "orange";

/**
 * Visitor-bucket region keys for trade-page copy. Aligns with the
 * ISO alpha-2 codes the rest of the codebase uses (`profile.country`,
 * `visitorGeo.country`, `getFormatsForCountry`). GB / US / CA are the
 * launch markets with explicit doc-supplied copy variants. EU and ROW
 * are coarse fallback buckets for the rest of the world; they share
 * the GB copy until per-region variants are written.
 */
export type TradeRegion = "GB" | "US" | "CA" | "EU" | "ROW";

/** Region-keyed value with a GB fallback semantic baked into the
 *  resolver (`resolveLocalized`). Every trade defines an entry for
 *  GB/US/CA; EU and ROW callers fall back to GB. */
export type LocalizedTradeField<T> = Partial<Record<TradeRegion, T>>;

export interface TradePainHero {
  region: TradeRegion;
  /** What the trader actually says when they're venting -- 1-2
   *  sentences, real-vernacular, no Manos attribution. */
  problem: string;
  /** How Manos addresses it. When `solutionPunchline` is also set,
   *  this is the intro half (renders in foreground, like the hero
   *  headline's intro). When solutionPunchline is omitted, the
   *  whole solution renders in the trade-accent colour. */
  solution: string;
  /** Optional punchline that picks up the trade-accent colour --
   *  same pattern as `heroHeadline.punchline`. Use when the
   *  solution has a natural before/after sentence split where the
   *  closer is the dramatic beat. */
  solutionPunchline?: string;
}

export interface MockAppScreenContent {
  title: string;
  subtitle: string;
  iconKey: "lightning" | "inbox" | "calendar" | "receipt";
  items: { title: string; stage: string; color: string }[];
}

export interface TradeWorkflow {
  title: string;
  steps: string[];
}

export interface TradeBenefit {
  title: string;
  body: string;
}

export interface TradeFaq {
  q: string;
  a: string;
}

export interface TradeHeroHeadline {
  intro: string;
  punchline: string;
}

export interface TradeContent {
  slug: string;
  name: string;
  plural: string;
  /** Optional per-region override of `name` for routes that read
   *  awkwardly across markets. Loader resolves against the visitor's
   *  region; falls back to `name` when no override is present. */
  displayName?: LocalizedTradeField<string>;
  /** Page-chrome accent (gradient blur + mock-screen + stats tint). */
  accentColour: TradeAccent;
  /** Hero headline split into two parts so the punchline can render
   *  in the trade-accent colour with a heavier font weight. */
  heroHeadline: LocalizedTradeField<TradeHeroHeadline>;
  heroSub: LocalizedTradeField<string>;
  /** The headline pain for "Sound familiar?". Region-keyed array; the
   *  per-trade page picks the entry matching the visitor (GB / US /
   *  CA / EU / ROW) and falls back to the first available. Omit to
   *  hide the hero callout entirely. */
  painHeros?: TradePainHero[];
  /** Three supporting pains in a 3-col grid below the hero. Always
   *  exactly three per region -- the layout breaks visually with
   *  four. */
  painPoints: LocalizedTradeField<string[]>;
  benefits: LocalizedTradeField<TradeBenefit[]>;
  workflow: LocalizedTradeField<TradeWorkflow>;
  faqs?: LocalizedTradeField<TradeFaq[]>;
  /** Mock app screen on the right of the hero at lg+. */
  mockAppScreen: LocalizedTradeField<MockAppScreenContent>;
  /** Optional 4-column stats band below the hero. Falls back to the
   *  generic Manos stats if omitted. */
  statsBand?: { value: string; label: string }[];
  /** Slot-label noun used on the BookingPageHero CTA -- "appointment"
   *  / "session" / "lesson" / "call-out" / "visit" / "groom". */
  bookingHeroSlotLabel?: string;
  /** Plausible per-job ticket size shown on the TapToPayCallout phone
   *  mock, as a numeric amount. Loader formats it with the visitor's
   *  geo so a US visitor sees `$120.00`, a UK visitor sees `£120.00`,
   *  etc. Region-keyed map preferred (a US plumbing job typically
   *  costs more than a UK one); single-number form retained as a
   *  back-compat fallback during migration. */
  tapToPayTicketAmount?: number | LocalizedTradeField<number>;
  /** Deprecated -- pre-formatted ticket string. Kept as a fallback
   *  while every trade migrates to tapToPayTicketAmount. Once all
   *  trades have an amount, drop this field and the resolver path. */
  tapToPayTicket?: string;
  /** Short label shown on the TapToPayCallout phone mock under the
   *  "Job complete" line. Optional. */
  tapToPayLabel?: string;
}

export const TRADES: TradeContent[] = [
  {
    slug: "plumbers",
    name: "Plumber",
    plural: "Plumbers",
    accentColour: "blue",
    tapToPayTicketAmount: { GB: 120, US: 180, CA: 220 },
    tapToPayTicket: "£120.00",
    tapToPayLabel: "Job complete -- tap to pay",
    heroHeadline: {
      GB: {
        intro: "Plumbing jobs, booked and paid",
        punchline: "before you're off the van.",
      },
      US: {
        intro: "Plumbing jobs, booked and paid",
        punchline: "before you're off the truck.",
      },
      CA: {
        intro: "Plumbing jobs, booked and paid",
        punchline: "before you're off the truck.",
      },
    },
    heroSub: {
      GB: "From leaky tap to boiler install, Manos keeps the admin off your tools. Quote on-site, send the invoice before you drive off, and see the payment land by the time you're home.",
      US: "From leaky faucet to water heater install, Manos keeps the admin off your tools. Quote on-site, send the invoice before you drive off, and see the payment land by the time you're home.",
      CA: "From leaky faucet to water heater install, Manos keeps the admin off your tools. Quote on-site, send the invoice before you drive off, and see the payment land by the time you're home.",
    },
    mockAppScreen: {
      GB: {
        title: "Inbox",
        subtitle: "3 items need attention",
        iconKey: "lightning",
        items: [
          { title: "Mrs Patel -- bath trap leak", stage: "Enquiry", color: "#6B7280" },
          { title: "Marsh House Lettings -- annual safety", stage: "Quoted", color: "#F59E0B" },
          { title: "Sarah Chen -- emergency leak", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
      US: {
        title: "Inbox",
        subtitle: "3 items need attention",
        iconKey: "lightning",
        items: [
          { title: "Mrs Patel -- P-trap leak", stage: "Inquiry", color: "#6B7280" },
          { title: "Marsh House Property Mgmt -- water heater service", stage: "Quoted", color: "#F59E0B" },
          { title: "Sarah Chen -- emergency leak", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
      CA: {
        title: "Inbox",
        subtitle: "3 items need attention",
        iconKey: "lightning",
        items: [
          { title: "Mrs Patel -- P-trap leak", stage: "Inquiry", color: "#6B7280" },
          { title: "Marsh House Property Mgmt -- water heater service", stage: "Quoted", color: "#F59E0B" },
          { title: "Sarah Chen -- emergency leak", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "I quoted a Sunday 7am job, drove out, gave the guy a fair price. Took him 3 hours to ring back saying he'd found someone 400 quid cheaper.",
        solution:
          "Lock in the deposit before you leave the van. If they want to shop around, fine.",
        solutionPunchline: "They're not wasting your morning anymore.",
      },
      {
        region: "US",
        problem:
          "I quoted a Sunday 7am job, drove out, gave the guy a fair price. Took him 3 hours to call back saying he'd found someone $600 cheaper.",
        solution:
          "Lock in the deposit before you leave the truck. If they want to shop around, fine.",
        solutionPunchline: "They're not wasting your morning anymore.",
      },
      {
        region: "CA",
        problem:
          "I quoted a Sunday 7am job, drove out, gave the guy a fair price. Took him 3 hours to call back saying he'd found someone $700 cheaper.",
        solution:
          "Lock in the deposit before you leave the truck. If they want to shop around, fine.",
        solutionPunchline: "They're not wasting your morning anymore.",
      },
    ],
    painPoints: {
      GB: [
        "Losing the notebook you wrote a quote on at the customer's kitchen table",
        "Forgetting who you promised a follow-up visit to after a busy day",
        "Missing the reminder for the landlord's annual gas safety check",
      ],
      US: [
        "Losing the notebook you wrote a quote on at the customer's kitchen table",
        "Forgetting who you promised a follow-up visit to after a busy day",
        "Missing the reminder for the landlord's annual water heater service",
      ],
      CA: [
        "Losing the notebook you wrote a quote on at the customer's kitchen table",
        "Forgetting who you promised a follow-up visit to after a busy day",
        "Missing the reminder for the landlord's annual water heater service",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Voice-capture jobs between houses",
          body: "Dictate the next job while you're packing up. 'Mrs Patel, replace bath trap, Thursday morning' becomes a scheduled appointment with the client attached.",
        },
        {
          title: "Parts lists built into quotes",
          body: "Save reusable quote templates for the work you do most -- boiler service, bathroom rewire, radiator swap. One tap to apply, five seconds to customise.",
        },
        {
          title: "Annual safety-check reminders",
          body: "Tag a job as gas-safety and Manos pings you in your dashboard a month before the cert expires.",
        },
        {
          title: "One-tap invoicing after the job",
          body: "Mark a job complete and Manos drafts the invoice ready to send. Cash, bank transfer, and card paid via Stripe all reconcile in your books.",
        },
      ],
      US: [
        {
          title: "Voice-capture jobs between houses",
          body: "Dictate the next job while you're packing up. 'Mrs Patel, replace P-trap under the tub, Thursday morning' becomes a scheduled appointment with the client attached.",
        },
        {
          title: "Parts lists built into quotes",
          body: "Save reusable quote templates for the work you do most -- water heater service, bathroom remodel, fixture swap. One tap to apply, five seconds to customize.",
        },
        {
          title: "Annual service reminders",
          body: "Tag a job as water-heater-service and Manos pings you in your dashboard a month before it's due to come round again.",
        },
        {
          title: "One-tap invoicing after the job",
          body: "Mark a job complete and Manos drafts the invoice ready to send. Cash, ACH, check, and card paid via Stripe all reconcile in your books.",
        },
      ],
      CA: [
        {
          title: "Voice-capture jobs between houses",
          body: "Dictate the next job while you're packing up. 'Mrs Patel, replace P-trap under the tub, Thursday morning' becomes a scheduled appointment with the client attached.",
        },
        {
          title: "Parts lists built into quotes",
          body: "Save reusable quote templates for the work you do most -- water heater service, bathroom remodel, fixture swap. One tap to apply, five seconds to customize.",
        },
        {
          title: "Annual service reminders",
          body: "Tag a job as water-heater-service and Manos pings you in your dashboard a month before it's due to come round again.",
        },
        {
          title: "One-tap invoicing after the job",
          body: "Mark a job complete and Manos drafts the invoice ready to send. Cash, e-Transfer, cheque, and card paid via Stripe all reconcile in your books.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical day",
        steps: [
          "Morning: check your inbox. Two quote requests from last night, three upcoming jobs today.",
          "On the way: voice-capture the emergency call-out the letting agent rang about. Quick Capture puts it on the schedule -- the morning-of reminder fires automatically before you arrive.",
          "At the job: price job using a saved template, send quote from the van, customer approves on their phone before you've finished the cuppa.",
          "Evening: three invoices sent, two paid, next year's boiler services pinged on your dashboard so you can rebook them.",
        ],
      },
      US: {
        title: "A typical day",
        steps: [
          "Morning: check your inbox. Two quote requests from last night, three upcoming jobs today.",
          "On the way: voice-capture the emergency call-out the property manager called about. Quick Capture puts it on the schedule -- the morning-of reminder fires automatically before you arrive.",
          "At the job: price the job using a saved template, send quote from the truck, customer approves on their phone before you've packed up.",
          "Evening: three invoices sent, two paid, next year's water heater services pinged on your dashboard so you can rebook them.",
        ],
      },
      CA: {
        title: "A typical day",
        steps: [
          "Morning: check your inbox. Two quote requests from last night, three upcoming jobs today.",
          "On the way: voice-capture the emergency call-out the property manager called about. Quick Capture puts it on the schedule -- the morning-of reminder fires automatically before you arrive.",
          "At the job: price the job using a saved template, send quote from the truck, customer approves on their phone before you've packed up.",
          "Evening: three invoices sent, two paid, next year's water heater services pinged on your dashboard so you can rebook them.",
        ],
      },
    },
    faqs: {
      GB: [
        {
          q: "Does Manos handle gas safety certificates?",
          a: "Tag a job as gas-safety. Manos tracks the 12-month expiry and pings you in the dashboard a month before. Cert PDF storage is on the roadmap (#122).",
        },
        {
          q: "Can I add materials to an invoice on-site?",
          a: "Yes. Add parts on the phone as you fit them, prices sync to the invoice automatically. No more scribbling on the back of a receipt.",
        },
      ],
      US: [
        {
          q: "Does Manos handle service reminders?",
          a: "Tag a job with its service interval (12-month water heater flush, annual backflow test, etc) and Manos pings you in the dashboard a month before. Service-record PDF storage is on the roadmap (#122).",
        },
        {
          q: "Can I add materials to an invoice on-site?",
          a: "Yes. Add parts on the phone as you fit them, prices sync to the invoice automatically. No more scribbling on the back of a receipt.",
        },
      ],
      CA: [
        {
          q: "Does Manos handle service reminders?",
          a: "Tag a job with its service interval (12-month water heater flush, annual backflow test, etc) and Manos pings you in the dashboard a month before. Service-record PDF storage is on the roadmap (#122).",
        },
        {
          q: "Can I add materials to an invoice on-site?",
          a: "Yes. Add parts on the phone as you fit them, prices sync to the invoice automatically. No more scribbling on the back of a receipt.",
        },
      ],
    },
  },
  {
    slug: "electricians",
    name: "Electrician",
    plural: "Electricians",
    accentColour: "orange",
    tapToPayTicketAmount: { GB: 90, US: 145, CA: 180 },
    tapToPayTicket: "£90.00",
    tapToPayLabel: "Job complete -- tap to pay",
    // [research needed: equivalent of EICR in US/CA]. Generic copy
    // uses "inspection report" / "panel inspection" / "electrical
    // safety inspection" -- regional certification names (NEC
    // compliance docs in US, CSA-aligned permit inspections in CA)
    // vary by state/province. Confirm with a domain-expert pass
    // before pushing harder regulatory references.
    heroHeadline: {
      GB: { intro: "Inspections, installs, emergencies.", punchline: "All in one place." },
      US: { intro: "Inspections, installs, emergencies.", punchline: "All in one place." },
      CA: { intro: "Inspections, installs, emergencies.", punchline: "All in one place." },
    },
    heroSub: {
      GB: "Track certificates, schedule remedial work, and bill for it -- without swapping between three apps. Built for sole-trader sparks and small firms running three vans.",
      US: "Track inspections, schedule remedial work, and bill for it -- without swapping between three apps. Built for solo electricians and small firms running three trucks.",
      CA: "Track inspections, schedule remedial work, and bill for it -- without swapping between three apps. Built for solo electricians and small firms running three trucks.",
    },
    mockAppScreen: {
      GB: {
        title: "Inbox",
        subtitle: "Inspections + remedial work",
        iconKey: "lightning",
        items: [
          { title: "Flat 4, Rosehill -- safety check due", stage: "Renewal", color: "#F97316" },
          { title: "Mr Khan -- consumer unit", stage: "Quoted", color: "#F59E0B" },
          { title: "Westview Lodge -- remedials", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
      US: {
        title: "Inbox",
        subtitle: "Inspections + remedial work",
        iconKey: "lightning",
        items: [
          { title: "Apt 4, Rosehill -- inspection due", stage: "Re-test", color: "#F97316" },
          { title: "Mr Khan -- panel upgrade", stage: "Quoted", color: "#F59E0B" },
          { title: "Westview Apartments -- remedials", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
      CA: {
        title: "Inbox",
        subtitle: "Inspections + remedial work",
        iconKey: "lightning",
        items: [
          { title: "Apt 4, Rosehill -- inspection due", stage: "Re-test", color: "#F97316" },
          { title: "Mr Khan -- panel upgrade", stage: "Quoted", color: "#F59E0B" },
          { title: "Westview Apartments -- remedials", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Letting agent's asking for the inspection cert you did 18 months ago. They've already messaged three other sparks too. Whoever replies first wins the next job.",
        solution:
          "Every cert sits against the property. Reply with a secure link in five seconds, ask them what else needs a look.",
        solutionPunchline: "First name they think of next month.",
      },
      {
        region: "US",
        problem:
          "Property manager's asking for the inspection report you did 18 months ago. They've already texted three other electricians. Whoever replies first wins the next job.",
        solution:
          "Every report sits against the property. Reply with a secure link in five seconds, ask them what else needs a look.",
        solutionPunchline: "First name they think of next month.",
      },
      {
        region: "CA",
        problem:
          "Property manager's asking for the inspection report you did 18 months ago. They've already texted three other electricians. Whoever replies first wins the next job.",
        solution:
          "Every report sits against the property. Reply with a secure link in five seconds, ask them what else needs a look.",
        solutionPunchline: "First name they think of next month.",
      },
    ],
    painPoints: {
      GB: [
        "Losing track of which inspections are due for renewal",
        "Forgetting to invoice the remedial work after the inspection",
        "Managing certificates for a dozen landlord portfolios at once",
      ],
      US: [
        "Losing track of which inspections are due for re-test",
        "Forgetting to invoice the remedial work after the inspection",
        "Managing reports for a dozen landlord portfolios at once",
      ],
      CA: [
        "Losing track of which inspections are due for re-test",
        "Forgetting to invoice the remedial work after the inspection",
        "Managing reports for a dozen landlord portfolios at once",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Renewal calendar for inspections",
          body: "Every inspection you issue is tagged with its expiry. Filter your inspections by 'expiring in the next 30 days' so you can book the work before the customer panics. Auto-digest of these is on the roadmap (#118).",
        },
        {
          title: "Certificate PDFs stored against the property",
          body: "No more digging through emails. Every cert lives with the property record, downloadable by you and the owner.",
        },
        {
          title: "Quote templates by work type",
          body: "Standard CU replacement, EV charger install, full rewire -- save the templates once, use them forever.",
        },
        {
          title: "Recurring inspections auto-schedule",
          body: "Tag commercial inspections with their renewal cycle. Manos pings you in the dashboard a month before. Auto-rebook is on the roadmap (#119).",
        },
      ],
      US: [
        {
          title: "Re-test calendar for inspections",
          body: "Every inspection report is tagged with its next-due date. Filter your inspections by 'due in the next 30 days' so you can book the work before the customer panics. Auto-digest of these is on the roadmap (#118).",
        },
        {
          title: "Inspection reports stored against the property",
          body: "No more digging through emails. Every report lives with the property record, downloadable by you and the owner.",
        },
        {
          title: "Quote templates by work type",
          body: "Panel upgrade, EV charger install, full rewire, generator transfer switch -- save the templates once, use them forever.",
        },
        {
          title: "Recurring inspections auto-schedule",
          body: "Tag commercial inspections with their re-test cycle. Manos pings you in the dashboard a month before. Auto-rebook is on the roadmap (#119).",
        },
      ],
      CA: [
        {
          title: "Re-test calendar for inspections",
          body: "Every inspection report is tagged with its next-due date. Filter your inspections by 'due in the next 30 days' so you can book the work before the customer panics. Auto-digest of these is on the roadmap (#118).",
        },
        {
          title: "Inspection reports stored against the property",
          body: "No more digging through emails. Every report lives with the property record, downloadable by you and the owner.",
        },
        {
          title: "Quote templates by work type",
          body: "Panel upgrade, EV charger install, full rewire, generator transfer switch -- save the templates once, use them forever.",
        },
        {
          title: "Recurring inspections auto-schedule",
          body: "Tag commercial inspections with their re-test cycle. Manos pings you in the dashboard a month before. Auto-rebook is on the roadmap (#119).",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical week",
        steps: [
          "Monday: filter inspections by 'expiring this month'. Phone or email the customers to book, five minutes done.",
          "Wednesday: emergency consumer unit failure. Voice capture while you're on the way; the schedule entry sends a morning-of reminder before you arrive. Arrive prepped with a quote template.",
          "Friday: invoice the week's work. Manos has already tracked labour hours and parts from each job, you just review and send.",
          "Sunday: check next week. Six inspections, two remedial jobs, one commercial inspection.",
        ],
      },
      US: {
        title: "A typical week",
        steps: [
          "Monday: filter inspections by 'due this month'. Phone or email the customers to book, five minutes done.",
          "Wednesday: emergency panel failure. Voice capture while you're on the way; the schedule entry sends a morning-of reminder before you arrive. Arrive prepped with a quote template.",
          "Friday: invoice the week's work. Manos has already tracked labor hours and parts from each job, you just review and send.",
          "Sunday: check next week. Six inspections, two remedial jobs, one commercial inspection.",
        ],
      },
      CA: {
        title: "A typical week",
        steps: [
          "Monday: filter inspections by 'due this month'. Phone or email the customers to book, five minutes done.",
          "Wednesday: emergency panel failure. Voice capture while you're on the way; the schedule entry sends a morning-of reminder before you arrive. Arrive prepped with a quote template.",
          "Friday: invoice the week's work. Manos has already tracked labour hours and parts from each job, you just review and send.",
          "Sunday: check next week. Six inspections, two remedial jobs, one commercial inspection.",
        ],
      },
    },
  },
  {
    slug: "builders",
    name: "Builder",
    plural: "Builders",
    accentColour: "amber",
    tapToPayTicketAmount: { GB: 450, US: 600, CA: 750 },
    tapToPayTicket: "£450.00",
    tapToPayLabel: "Stage 1 done -- tap to pay",
    heroHeadline: {
      GB: { intro: "From day-rate work to full extensions,", punchline: "kept tidy." },
      US: { intro: "From day-rate work to full additions,", punchline: "kept tidy." },
      CA: { intro: "From day-rate work to full additions,", punchline: "kept tidy." },
    },
    heroSub: {
      GB: "Manage multi-week projects, stage payments, and subcontractor bookings without losing track. Whether you're a sole builder or running a crew of five, Manos keeps the paperwork where it belongs -- out of your way.",
      US: "Manage multi-week projects, stage payments, and subcontractor bookings without losing track. Whether you're a solo GC or running a crew of five, Manos keeps the paperwork where it belongs -- out of your way.",
      CA: "Manage multi-week projects, stage payments, and subcontractor bookings without losing track. Whether you're a solo GC or running a crew of five, Manos keeps the paperwork where it belongs -- out of your way.",
    },
    mockAppScreen: {
      GB: {
        title: "Jobs in flight",
        subtitle: "3 sites this week",
        iconKey: "lightning",
        items: [
          { title: "Daniels -- side return extension", stage: "Quoted", color: "#F59E0B" },
          { title: "Mr Foster -- kitchen install", stage: "On site", color: "#F97316" },
          { title: "Hilltop Cottage -- snags", stage: "Final week", color: "#10B981" },
        ],
      },
      US: {
        title: "Jobs in flight",
        subtitle: "3 sites this week",
        iconKey: "lightning",
        items: [
          { title: "Daniels -- rear addition", stage: "Quoted", color: "#F59E0B" },
          { title: "Mr Foster -- kitchen remodel", stage: "On site", color: "#F97316" },
          { title: "Hilltop Cottage -- punch list", stage: "Final week", color: "#10B981" },
        ],
      },
      CA: {
        title: "Jobs in flight",
        subtitle: "3 sites this week",
        iconKey: "lightning",
        items: [
          { title: "Daniels -- rear addition", stage: "Quoted", color: "#F59E0B" },
          { title: "Mr Foster -- kitchen remodel", stage: "On site", color: "#F97316" },
          { title: "Hilltop Cottage -- punch list", stage: "Final week", color: "#10B981" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Every winter the work dries up. December comes, the kids want presents, and I'm calling old customers hoping they've got something.",
        solution: "Pre-schedule the next round of work before the gap shows up.",
        solutionPunchline: "Not a week before Christmas.",
      },
      {
        region: "US",
        problem:
          "Every winter the work dries up. December comes, the kids want presents, and I'm calling old customers hoping they've got something.",
        solution: "Pre-schedule the next round of work before the gap shows up.",
        solutionPunchline: "Not a week before Christmas.",
      },
      {
        region: "CA",
        problem:
          "Every winter the work dries up. December comes, the kids want presents, and I'm calling old customers hoping they've got something.",
        solution: "Pre-schedule the next round of work before the gap shows up.",
        solutionPunchline: "Not a week before Christmas.",
      },
    ],
    painPoints: {
      GB: [
        "Stage-payment schedules that live in your head",
        "Losing track of which subbie is on which site",
        "Invoicing after the job when half the receipts have walked off",
      ],
      US: [
        "Draw schedules that live in your head",
        "Losing track of which sub is on which site",
        "Invoicing after the job when half the receipts have walked off",
      ],
      CA: [
        "Draw schedules that live in your head",
        "Losing track of which sub is on which site",
        "Invoicing after the job when half the receipts have walked off",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Multi-stage quotes",
          body: "Break a job into deposit, first-fix, second-fix, final payment. Mark each stage done and Manos drafts the invoice ready to send.",
        },
        {
          title: "Project tags for subbies and materials",
          body: "Tag jobs with which subcontractor is booked, which materials are on order, which are in the yard. Filter the schedule to see it at a glance.",
        },
        {
          title: "Expense capture from receipts",
          body: "Snap a timber-merchant receipt, Manos reads the totals and pins it to the right job. No more shoe box full of crumpled till rolls.",
        },
      ],
      US: [
        {
          title: "Multi-draw quotes",
          body: "Break a job into deposit, rough-in, drywall, final draw. Mark each stage done and Manos drafts the invoice ready to send.",
        },
        {
          title: "Project tags for subs and materials",
          body: "Tag jobs with which subcontractor is booked, which materials are on order, which are on the lot. Filter the schedule to see it at a glance.",
        },
        {
          title: "Expense capture from receipts",
          body: "Snap a lumberyard receipt, Manos reads the totals and pins it to the right job. No more shoe box full of crumpled receipts.",
        },
      ],
      CA: [
        {
          title: "Multi-draw quotes",
          body: "Break a job into deposit, rough-in, drywall, final draw. Mark each stage done and Manos drafts the invoice ready to send.",
        },
        {
          title: "Project tags for subs and materials",
          body: "Tag jobs with which subcontractor is booked, which materials are on order, which are on the lot. Filter the schedule to see it at a glance.",
        },
        {
          title: "Expense capture from receipts",
          body: "Snap a lumberyard receipt, Manos reads the totals and pins it to the right job. No more shoe box full of crumpled receipts.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical project",
        steps: [
          "Quote stage: capture the brief, build a staged quote, send for approval.",
          "Build stage: tag days against the job, snap photos as you go, log expenses from merchants.",
          "Invoice stage: mark each completed stage done and Manos drafts the invoice ready to send.",
          "Handover: stash the photos, final invoice, and any warranty info on the customer record.",
        ],
      },
      US: {
        title: "A typical project",
        steps: [
          "Quote stage: capture the scope, build a staged quote, send for approval.",
          "Build stage: tag days against the job, snap photos as you go, log expenses from suppliers.",
          "Invoice stage: mark each completed draw done and Manos drafts the invoice ready to send.",
          "Handover: stash the photos, final invoice, and any warranty info on the customer record.",
        ],
      },
      CA: {
        title: "A typical project",
        steps: [
          "Quote stage: capture the scope, build a staged quote, send for approval.",
          "Build stage: tag days against the job, snap photos as you go, log expenses from suppliers.",
          "Invoice stage: mark each completed draw done and Manos drafts the invoice ready to send.",
          "Handover: stash the photos, final invoice, and any warranty info on the customer record.",
        ],
      },
    },
  },
  {
    slug: "gas-engineers",
    name: "Gas engineer",
    plural: "Gas engineers",
    // "Gas engineer" is a UK term; the role splits across HVAC tech
    // and plumber-with-gas-license in US/CA. Slug is fixed; per-
    // region displayName retitles the page heading + meta tags so a
    // North American visitor sees "Furnace tech" instead.
    displayName: {
      GB: "Gas engineer",
      US: "Furnace tech",
      CA: "Furnace tech",
    },
    accentColour: "orange",
    tapToPayTicketAmount: { GB: 180, US: 240, CA: 300 },
    tapToPayTicket: "£180.00",
    tapToPayLabel: "Service done -- tap to pay",
    // [research needed: NATE is the most cited US HVAC/gas
    // certification but state-level licensing varies; TSSA is
    // Ontario-specific in CA -- BC uses "Gas Fitter ticket A/B" --
    // consider keeping the cert name generic ("certification") if a
    // single province-neutral term is preferred].
    heroHeadline: {
      GB: { intro: "Boiler services, safety checks, landlord compliance.", punchline: "All handled." },
      US: { intro: "Furnace tune-ups, gas line work, service contracts.", punchline: "All handled." },
      CA: { intro: "Furnace tune-ups, gas line work, service contracts.", punchline: "All handled." },
    },
    heroSub: {
      GB: "Annual gas safety checks are the backbone of your book. Manos makes sure you never miss one, never double-book one, and never have to dig for the last year's certificate when the letting agent calls.",
      US: "Annual furnace tune-ups and gas line inspections are the backbone of your book. Manos makes sure you never miss one, never double-book one, and never have to dig for last year's service record when the property manager calls.",
      CA: "Annual furnace tune-ups and gas line inspections are the backbone of your book. Manos makes sure you never miss one, never double-book one, and never have to dig for last year's service record when the property manager calls.",
    },
    mockAppScreen: {
      GB: {
        title: "Today's calls",
        subtitle: "4 boilers + an emergency",
        iconKey: "calendar",
        items: [
          { title: "9:30am -- Mrs Patel boiler service", stage: "Service", color: "#3B82F6" },
          { title: "12:00pm -- Westgate Lettings annual", stage: "Safety check", color: "#F59E0B" },
          { title: "4:00pm -- Henderson no heat", stage: "Emergency", color: "#EF4444" },
        ],
      },
      US: {
        title: "Today's calls",
        subtitle: "4 furnaces + an emergency",
        iconKey: "calendar",
        items: [
          { title: "9:30am -- Mrs Patel furnace tune-up", stage: "Service", color: "#3B82F6" },
          { title: "12:00pm -- Westgate Property Mgmt annual", stage: "Tune-up", color: "#F59E0B" },
          { title: "4:00pm -- Henderson no heat", stage: "Emergency", color: "#EF4444" },
        ],
      },
      CA: {
        title: "Today's calls",
        subtitle: "4 furnaces + an emergency",
        iconKey: "calendar",
        items: [
          { title: "9:30am -- Mrs Patel furnace tune-up", stage: "Service", color: "#3B82F6" },
          { title: "12:00pm -- Westgate Property Mgmt annual", stage: "Tune-up", color: "#F59E0B" },
          { title: "4:00pm -- Henderson no heat", stage: "Emergency", color: "#EF4444" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "My Gas Safe renewal nearly lapsed last year because I lost track of the paperwork. Half my Saturdays used to be hunting for cert PDFs.",
        solution:
          "Every job sheet, certificate and reminder lives in one place.",
        solutionPunchline: "Saturdays go back to you, not the paperwork.",
      },
      {
        region: "US",
        problem:
          "My NATE recert almost lapsed last year because I lost track of the paperwork. Half my Saturdays used to be hunting for service-record PDFs.",
        solution:
          "Every job sheet, service record and reminder lives in one place.",
        solutionPunchline: "Saturdays go back to you, not the paperwork.",
      },
      {
        region: "CA",
        problem:
          "My TSSA renewal almost lapsed last year because I lost track of the paperwork. Half my Saturdays used to be hunting for service-record PDFs.",
        solution:
          "Every job sheet, service record and reminder lives in one place.",
        solutionPunchline: "Saturdays go back to you, not the paperwork.",
      },
    ],
    painPoints: {
      GB: [
        "Annual safety renewals for 40+ landlord properties",
        "Lettings agents asking for old certificates at the worst times",
        "Breakdown call-outs breaking a full diary",
      ],
      US: [
        "Annual service renewals for 40+ rental properties",
        "Property managers asking for old service records at the worst times",
        "Breakdown call-outs breaking a full schedule",
      ],
      CA: [
        "Annual service renewals for 40+ rental properties",
        "Property managers asking for old service records at the worst times",
        "Breakdown call-outs breaking a full schedule",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Safety-check renewal pipeline",
          body: "Every Gas Safe certificate you log has an expiry tracked against the property. See this month, next month, and overdue at a glance.",
        },
        {
          title: "Breakdown slots that don't break the week",
          body: "Quick-book emergency slots without a calendar dance.",
        },
        {
          title: "Parts and labour priced on-site",
          body: "Save quotes for the boilers you fit most often. New Worcester Greenstar? Three taps, off the van.",
        },
      ],
      US: [
        {
          title: "Service renewal pipeline",
          body: "Every annual tune-up you log has its next-due date tracked against the property. See this month, next month, and overdue at a glance.",
        },
        {
          title: "Breakdown slots that don't break the week",
          body: "Quick-book emergency slots without a calendar dance.",
        },
        {
          title: "Parts and labor priced on-site",
          body: "Save quotes for the furnaces you fit most often. New Trane XV80? Three taps, off the truck.",
        },
      ],
      CA: [
        {
          title: "Service renewal pipeline",
          body: "Every annual tune-up you log has its next-due date tracked against the property. See this month, next month, and overdue at a glance.",
        },
        {
          title: "Breakdown slots that don't break the week",
          body: "Quick-book emergency slots without a calendar dance.",
        },
        {
          title: "Parts and labour priced on-site",
          body: "Save quotes for the furnaces you fit most often. New Lennox EL296V? Three taps, off the truck.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "Landlord renewal flow",
        steps: [
          "Filter your jobs by 'safety check expiring this month'. Twelve properties show up.",
          "Tap each to schedule a visit. Tenant gets a booking email. (Booking SMS is on the roadmap, #120.)",
          "On-site: log the cert against the property. PDF generation + auto-share to the customer is on the roadmap, #122.",
          "Mark the job done. Manos drafts the invoice ready to send. Tap-to-pay if the customer wants card on the doorstep.",
        ],
      },
      US: {
        title: "Rental tune-up flow",
        steps: [
          "Filter your jobs by 'tune-up due this month'. Twelve properties show up.",
          "Tap each to schedule a visit. Tenant gets a booking email. (Booking SMS is on the roadmap, #120.)",
          "On-site: log the service record against the property. PDF generation + auto-share to the customer is on the roadmap, #122.",
          "Mark the job done. Manos drafts the invoice ready to send. Tap-to-pay if the customer wants card on the doorstep.",
        ],
      },
      CA: {
        title: "Rental tune-up flow",
        steps: [
          "Filter your jobs by 'tune-up due this month'. Twelve properties show up.",
          "Tap each to schedule a visit. Tenant gets a booking email. (Booking SMS is on the roadmap, #120.)",
          "On-site: log the service record against the property. PDF generation + auto-share to the customer is on the roadmap, #122.",
          "Mark the job done. Manos drafts the invoice ready to send. Tap-to-pay if the customer wants card on the doorstep.",
        ],
      },
    },
  },
  {
    slug: "hvac-engineers",
    name: "HVAC engineer",
    plural: "HVAC engineers",
    accentColour: "orange",
    tapToPayTicketAmount: { GB: 240, US: 350, CA: 450 },
    tapToPayTicket: "$240.00",
    tapToPayLabel: "Service done -- tap to pay",
    // [research needed: refrigerant tracking regulation. EPA 608 is
    // the US baseline. CA equivalent is the Ozone-Depleting
    // Substances and Halocarbon Alternatives Regulations (ODSHFCR)
    // federally; provincial training varies. Confirm before
    // pushing harder regulatory references in the painPoints copy.]
    heroHeadline: {
      GB: { intro: "Service calls, installs, emergency heat-fail.", punchline: "All in one calendar." },
      US: { intro: "Service calls, installs, emergency heat-fail.", punchline: "All in one calendar." },
      CA: { intro: "Service calls, installs, emergency heat-fail.", punchline: "All in one calendar." },
    },
    heroSub: {
      GB: "Boiler service calls, AC replacements, heat-pump installs, and the 2am no-heat call -- Manos keeps your routes, your service tags, and your invoicing straight whether you're a sole HVAC tech or running three vans.",
      US: "Furnace tune-ups, AC replacements, heat-pump installs, and the 2am no-heat call -- Manos keeps your routes, your service tags, and your invoicing straight whether you're a sole HVAC tech or running three trucks.",
      CA: "Furnace tune-ups, AC replacements, heat-pump installs, and the 2am no-heat call -- Manos keeps your routes, your service tags, and your invoicing straight whether you're a sole HVAC tech or running three trucks.",
    },
    mockAppScreen: {
      GB: {
        title: "Today's calls",
        subtitle: "5 services + emergency",
        iconKey: "calendar",
        items: [
          { title: "9:00am -- Mrs Hayes boiler tune", stage: "Service", color: "#3B82F6" },
          { title: "1:00pm -- Westgate flats HVAC", stage: "Quoted", color: "#F59E0B" },
          { title: "4:30pm -- Henderson no heat", stage: "Emergency", color: "#EF4444" },
        ],
      },
      US: {
        title: "Today's calls",
        subtitle: "5 services + emergency",
        iconKey: "calendar",
        items: [
          { title: "9:00am -- Mrs Hayes furnace tune", stage: "Service", color: "#3B82F6" },
          { title: "1:00pm -- Westgate condo HVAC", stage: "Quoted", color: "#F59E0B" },
          { title: "4:30pm -- Henderson no heat", stage: "Emergency", color: "#EF4444" },
        ],
      },
      CA: {
        title: "Today's calls",
        subtitle: "5 services + emergency",
        iconKey: "calendar",
        items: [
          { title: "9:00am -- Mrs Hayes furnace tune", stage: "Service", color: "#3B82F6" },
          { title: "1:00pm -- Westgate condo HVAC", stage: "Quoted", color: "#F59E0B" },
          { title: "4:30pm -- Henderson no heat", stage: "Emergency", color: "#EF4444" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "I lost a furnace install quote in my texts last winter -- customer went elsewhere. By the time I found it I'd already lost the job.",
        solution:
          "Every quote, every part order, every service-tag history one search away.",
        solutionPunchline: "The 2am call doesn't catch you hunting.",
      },
      {
        region: "US",
        problem:
          "I lost a furnace install quote in my texts last winter -- customer went elsewhere. By the time I found it I'd already lost the job.",
        solution:
          "Every quote, every part order, every service-tag history one search away.",
        solutionPunchline: "The 2am call doesn't catch you hunting.",
      },
      {
        region: "CA",
        problem:
          "I lost a furnace install quote in my texts last winter -- customer went elsewhere. By the time I found it I'd already lost the job.",
        solution:
          "Every quote, every part order, every service-tag history one search away.",
        solutionPunchline: "The 2am call doesn't catch you hunting.",
      },
    ],
    painPoints: {
      GB: [
        "Annual service contracts that lapse because no one's tracking the renewal",
        "Refrigerant tracking + EPA logs you can't lose",
        "Emergency no-heat calls cratering your scheduled day",
      ],
      US: [
        "Annual service contracts that lapse because no one's tracking the renewal",
        "Refrigerant tracking + EPA 608 logs you can't lose",
        "Emergency no-heat calls cratering your scheduled day",
      ],
      CA: [
        "Annual service contracts that lapse because no one's tracking the renewal",
        "Refrigerant tracking + ODSHFCR logs you can't lose",
        "Emergency no-heat calls cratering your scheduled day",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Service-contract renewal calendar",
          body: "Mark a contract done and Manos pings you to rebook the next visit at the right time. No more hunting for who's due.",
        },
        {
          title: "Equipment history per property",
          body: "Furnace make + model + install year + last filter swap, all on the property record. Walk in knowing what you fitted.",
        },
        {
          title: "Staged-payment quotes",
          body: "Big system installs split into deposit, equipment, install, commissioning. Each stage triggers its own invoice when you mark it done.",
        },
        {
          title: "Emergency slots without breaking the schedule",
          body: "Tap to slot a no-heat call into today. No phone tag.",
        },
      ],
      US: [
        {
          title: "Service-contract renewal calendar",
          body: "Mark a contract done and Manos pings you to rebook the next visit at the right time. No more hunting for who's due.",
        },
        {
          title: "Equipment history per property",
          body: "Furnace make + model + install year + last filter swap, all on the property record. Walk in knowing what you installed.",
        },
        {
          title: "Staged-payment quotes",
          body: "Big system installs split into deposit, equipment, install, commissioning. Each stage triggers its own invoice when you mark it done.",
        },
        {
          title: "Emergency slots without breaking the schedule",
          body: "Tap to slot a no-heat call into today. No phone tag.",
        },
      ],
      CA: [
        {
          title: "Service-contract renewal calendar",
          body: "Mark a contract done and Manos pings you to rebook the next visit at the right time. No more hunting for who's due.",
        },
        {
          title: "Equipment history per property",
          body: "Furnace make + model + install year + last filter swap, all on the property record. Walk in knowing what you installed.",
        },
        {
          title: "Staged-payment quotes",
          body: "Big system installs split into deposit, equipment, install, commissioning. Each stage triggers its own invoice when you mark it done.",
        },
        {
          title: "Emergency slots without breaking the schedule",
          body: "Tap to slot a no-heat call into today. No phone tag.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical service week",
        steps: [
          "Monday: dashboard shows 8 maintenance contracts due this week.",
          "Tuesday: emergency no-heat call. Slot it in and re-arrange the day.",
          "Wednesday: heat-pump install quote -- staged, customer approves on their phone.",
          "Friday: invoice the week. Service contracts billed monthly, install jobs billed by stage.",
        ],
      },
      US: {
        title: "A typical service week",
        steps: [
          "Monday: dashboard shows 8 maintenance contracts due this week.",
          "Tuesday: emergency no-heat call. Slot it in and re-arrange the day.",
          "Wednesday: heat-pump install quote -- staged, customer approves on their phone.",
          "Friday: invoice the week. Service contracts billed monthly, install jobs billed by stage.",
        ],
      },
      CA: {
        title: "A typical service week",
        steps: [
          "Monday: dashboard shows 8 maintenance contracts due this week.",
          "Tuesday: emergency no-heat call. Slot it in and re-arrange the day.",
          "Wednesday: heat-pump install quote -- staged, customer approves on their phone.",
          "Friday: invoice the week. Service contracts billed monthly, install jobs billed by stage.",
        ],
      },
    },
  },
  {
    slug: "cleaners",
    name: "Cleaner",
    plural: "Cleaners",
    accentColour: "teal",
    bookingHeroSlotLabel: "visit",
    tapToPayTicketAmount: { GB: 45, US: 75, CA: 90 },
    tapToPayTicket: "£45.00",
    tapToPayLabel: "Clean complete -- tap to pay",
    heroHeadline: {
      GB: { intro: "Weekly rounds,", punchline: "running like clockwork." },
      US: { intro: "Weekly routes,", punchline: "running like clockwork." },
      CA: { intro: "Weekly routes,", punchline: "running like clockwork." },
    },
    heroSub: {
      GB: "Recurring cleans, end-of-tenancy deep cleans, Airbnb turnovers -- all on one schedule. Manos chases the invoices while you keep the mops moving.",
      US: "Recurring cleans, move-out deep cleans, Airbnb turnovers -- all on one schedule. Manos chases the invoices while you keep the mops moving.",
      CA: "Recurring cleans, move-out deep cleans, Airbnb turnovers -- all on one schedule. Manos chases the invoices while you keep the mops moving.",
    },
    mockAppScreen: {
      GB: {
        title: "This week",
        subtitle: "Recurring + one-offs",
        iconKey: "calendar",
        items: [
          { title: "Mon, Wed, Fri -- Stevens family home", stage: "Recurring", color: "#14B8A6" },
          { title: "Tuesday -- Bridge Street office", stage: "Weekly", color: "#3B82F6" },
          { title: "Saturday -- Patel deep clean", stage: "One-off", color: "#F59E0B" },
        ],
      },
      US: {
        title: "This week",
        subtitle: "Recurring + one-offs",
        iconKey: "calendar",
        items: [
          { title: "Mon, Wed, Fri -- Stevens family home", stage: "Recurring", color: "#14B8A6" },
          { title: "Tuesday -- Bridge Street office", stage: "Weekly", color: "#3B82F6" },
          { title: "Saturday -- Patel deep clean", stage: "One-off", color: "#F59E0B" },
        ],
      },
      CA: {
        title: "This week",
        subtitle: "Recurring + one-offs",
        iconKey: "calendar",
        items: [
          { title: "Mon, Wed, Fri -- Stevens family home", stage: "Recurring", color: "#14B8A6" },
          { title: "Tuesday -- Bridge Street office", stage: "Weekly", color: "#3B82F6" },
          { title: "Saturday -- Patel deep clean", stage: "One-off", color: "#F59E0B" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Cleaned a house from hell over two days, sent the invoice, then chased it for three weeks. By the end I'd given up.",
        solution:
          "Take a deposit before you show up. The rest goes through automatically.",
        solutionPunchline: "No three-week chase, no giving up.",
      },
      {
        region: "US",
        problem:
          "Cleaned a house from hell over two days, sent the invoice, then chased it for three weeks. By the end I'd given up.",
        solution:
          "Take a deposit before you show up. The rest goes through automatically.",
        solutionPunchline: "No three-week chase, no giving up.",
      },
      {
        region: "CA",
        problem:
          "Cleaned a house from hell over two days, sent the invoice, then chased it for three weeks. By the end I'd given up.",
        solution:
          "Take a deposit before you show up. The rest goes through automatically.",
        solutionPunchline: "No three-week chase, no giving up.",
      },
    ],
    painPoints: {
      GB: [
        "Juggling 30 weekly contracts in your head",
        "Rearranging when a customer goes on holiday",
        "Tenants wanting a specific time slot that clashes with your regulars",
      ],
      US: [
        "Juggling 30 weekly contracts in your head",
        "Rearranging when a customer goes on vacation",
        "Tenants wanting a specific time slot that clashes with your regulars",
      ],
      CA: [
        "Juggling 30 weekly contracts in your head",
        "Rearranging when a customer goes on vacation",
        "Tenants wanting a specific time slot that clashes with your regulars",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Weekly and fortnightly rotas",
          body: "Save a recurring rota for your regulars; track cancellations and reschedules in one calendar.",
        },
        {
          title: "Airbnb turnover windows",
          body: "Show up for the 11am checkout, done by 3pm check-in. Filter your day by property type so you can block the right window.",
        },
        {
          title: "Invoicing on complete",
          body: "Mark a clean done; Manos drafts the invoice ready to send.",
        },
        {
          title: "Customer self-reschedule links",
          body: "Send a reschedule link so customers can move their clean without having to WhatsApp you at 9pm.",
        },
      ],
      US: [
        {
          title: "Weekly and biweekly schedules",
          body: "Save a recurring schedule for your regulars; track cancellations and reschedules in one calendar.",
        },
        {
          title: "Airbnb turnover windows",
          body: "Show up for the 11am checkout, done by 3pm check-in. Filter your day by property type so you can block the right window.",
        },
        {
          title: "Invoicing on complete",
          body: "Mark a clean done; Manos drafts the invoice ready to send.",
        },
        {
          title: "Customer self-reschedule links",
          body: "Send a reschedule link so customers can move their clean without having to text you at 9pm.",
        },
      ],
      CA: [
        {
          title: "Weekly and biweekly schedules",
          body: "Save a recurring schedule for your regulars; track cancellations and reschedules in one calendar.",
        },
        {
          title: "Airbnb turnover windows",
          body: "Show up for the 11am checkout, done by 3pm check-in. Filter your day by property type so you can block the right window.",
        },
        {
          title: "Invoicing on complete",
          body: "Mark a clean done; Manos drafts the invoice ready to send.",
        },
        {
          title: "Customer self-reschedule links",
          body: "Send a reschedule link so customers can move their clean without having to text you at 9pm.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical week",
        steps: [
          "Monday morning: schedule already laid out -- 12 recurring rounds plus 3 end-of-tenancy cleans from last week.",
          "Tuesday: customer goes on holiday, she reschedules via the link herself.",
          "Friday: payments land. See this-month revenue on the dashboard.",
        ],
      },
      US: {
        title: "A typical week",
        steps: [
          "Monday morning: schedule already laid out -- 12 recurring routes plus 3 move-out cleans from last week.",
          "Tuesday: customer goes on vacation, she reschedules via the link herself.",
          "Friday: payments land. See this-month revenue on the dashboard.",
        ],
      },
      CA: {
        title: "A typical week",
        steps: [
          "Monday morning: schedule already laid out -- 12 recurring routes plus 3 move-out cleans from last week.",
          "Tuesday: customer goes on vacation, she reschedules via the link herself.",
          "Friday: payments land. See this-month revenue on the dashboard.",
        ],
      },
    },
  },
  {
    slug: "landscapers",
    name: "Landscaper",
    plural: "Landscapers",
    accentColour: "green",
    bookingHeroSlotLabel: "visit",
    tapToPayTicketAmount: { GB: 80, US: 120, CA: 150 },
    tapToPayTicket: "£80.00",
    tapToPayLabel: "Lawn done -- tap to pay",
    heroHeadline: {
      GB: { intro: "From mowing rounds", punchline: "to full garden builds." },
      US: { intro: "From mowing routes", punchline: "to full landscape builds." },
      CA: { intro: "From mowing routes", punchline: "to full landscape builds." },
    },
    heroSub: {
      GB: "Weekly maintenance rounds, one-off installs, and seasonal jobs -- on one simple schedule. Log the day's work, invoice by week or month, and never miss a recurring cut.",
      US: "Weekly maintenance routes, one-off installs, and seasonal jobs -- on one simple schedule. Log the day's work, invoice by week or month, and never miss a recurring cut.",
      CA: "Weekly maintenance routes, one-off installs, and seasonal jobs -- on one simple schedule. Log the day's work, invoice by week or month, and never miss a recurring cut.",
    },
    mockAppScreen: {
      GB: {
        title: "This week",
        subtitle: "3 lawns + a hedge",
        iconKey: "calendar",
        items: [
          { title: "Tuesday -- Mrs Patterson lawn", stage: "Fortnightly", color: "#10B981" },
          { title: "Thursday -- Hill House hedges", stage: "Quoted", color: "#F59E0B" },
          { title: "Saturday -- Jenkins garden tidy", stage: "One-off", color: "#3B82F6" },
        ],
      },
      US: {
        title: "This week",
        subtitle: "3 yards + a hedge",
        iconKey: "calendar",
        items: [
          { title: "Tuesday -- Mrs Patterson yard", stage: "Biweekly", color: "#10B981" },
          { title: "Thursday -- Hill House hedges", stage: "Quoted", color: "#F59E0B" },
          { title: "Saturday -- Jenkins yard cleanup", stage: "One-off", color: "#3B82F6" },
        ],
      },
      CA: {
        title: "This week",
        subtitle: "3 yards + a hedge",
        iconKey: "calendar",
        items: [
          { title: "Tuesday -- Mrs Patterson yard", stage: "Biweekly", color: "#10B981" },
          { title: "Thursday -- Hill House hedges", stage: "Quoted", color: "#F59E0B" },
          { title: "Saturday -- Jenkins yard cleanup", stage: "One-off", color: "#3B82F6" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Halfway through Mrs Pemberton's lawn. The phone goes. Stop the mower and you lose your line.",
        solution:
          "Voice-capture the enquiry from the saddle. Quote goes out before you've finished the cut.",
        solutionPunchline: "The mower never stops.",
      },
      {
        region: "US",
        problem:
          "Halfway through Mrs Pemberton's yard. The phone rings. Stop the mower and you lose your line.",
        solution:
          "Voice-capture the inquiry from the saddle. Quote goes out before you've finished the cut.",
        solutionPunchline: "The mower never stops.",
      },
      {
        region: "CA",
        problem:
          "Halfway through Mrs Pemberton's yard. The phone rings. Stop the mower and you lose your line.",
        solution:
          "Voice-capture the inquiry from the saddle. Quote goes out before you've finished the cut.",
        solutionPunchline: "The mower never stops.",
      },
    ],
    painPoints: {
      GB: [
        "Mowing rounds that grow by the month and become unmanageable",
        "Bigger build jobs losing money because materials weren't logged",
        "Being paid for half the maintenance rounds because invoices slip",
      ],
      US: [
        "Mowing routes that grow by the month and become unmanageable",
        "Bigger build jobs losing money because materials weren't logged",
        "Being paid for half the maintenance routes because invoices slip",
      ],
      CA: [
        "Mowing routes that grow by the month and become unmanageable",
        "Bigger build jobs losing money because materials weren't logged",
        "Being paid for half the maintenance routes because invoices slip",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Recurring mowing / maintenance rounds",
          body: "Save a recurring schedule for your weekly customers, see them all on one calendar. Skip-week handling built in.",
        },
        {
          title: "Materials on-site capture",
          body: "Log plants, turf, aggregate at the point of purchase. Costs attach to the job automatically.",
        },
      ],
      US: [
        {
          title: "Recurring mowing / maintenance routes",
          body: "Save a recurring schedule for your weekly customers, see them all on one calendar. Skip-week handling built in.",
        },
        {
          title: "Materials on-site capture",
          body: "Log plants, sod, aggregate at the point of purchase. Costs attach to the job automatically.",
        },
      ],
      CA: [
        {
          title: "Recurring mowing / maintenance routes",
          body: "Save a recurring schedule for your weekly customers, see them all on one calendar. Skip-week handling built in.",
        },
        {
          title: "Materials on-site capture",
          body: "Log plants, sod, aggregate at the point of purchase. Costs attach to the job automatically.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical week",
        steps: [
          "Monday: 18 weekly maintenance rounds on the schedule + 2 one-off jobs.",
          "Wednesday: garden build, log materials as you buy from the nursery.",
          "Friday: mark the week's mows complete; Manos drafts the one-off job invoice ready to send.",
        ],
      },
      US: {
        title: "A typical week",
        steps: [
          "Monday: 18 weekly maintenance routes on the schedule + 2 one-off jobs.",
          "Wednesday: landscape build, log materials as you buy from the nursery.",
          "Friday: mark the week's mows complete; Manos drafts the one-off job invoice ready to send.",
        ],
      },
      CA: {
        title: "A typical week",
        steps: [
          "Monday: 18 weekly maintenance routes on the schedule + 2 one-off jobs.",
          "Wednesday: landscape build, log materials as you buy from the nursery.",
          "Friday: mark the week's mows complete; Manos drafts the one-off job invoice ready to send.",
        ],
      },
    },
  },
  {
    slug: "painters",
    name: "Painter & decorator",
    plural: "Painters & decorators",
    // The "& decorator" tail is UK trade idiom; in US/CA the role
    // reads better as just "Painter". Slug stays "painters".
    displayName: {
      GB: "Painter & decorator",
      US: "Painter",
      CA: "Painter",
    },
    accentColour: "violet",
    tapToPayTicketAmount: { GB: 200, US: 300, CA: 380 },
    tapToPayTicket: "£200.00",
    tapToPayLabel: "Job done -- tap to pay",
    heroHeadline: {
      GB: { intro: "Every room logged, every customer chased,", punchline: "every invoice paid." },
      US: { intro: "Every room logged, every customer chased,", punchline: "every invoice paid." },
      CA: { intro: "Every room logged, every customer chased,", punchline: "every invoice paid." },
    },
    heroSub: {
      GB: "From one-room refreshes to full house repaints, Manos tracks the rooms done, the hours logged, and the final total -- so you're paid accurately every time.",
      US: "From one-room refreshes to full house repaints, Manos tracks the rooms done, the hours logged, and the final total -- so you're paid accurately every time.",
      CA: "From one-room refreshes to full house repaints, Manos tracks the rooms done, the hours logged, and the final total -- so you're paid accurately every time.",
    },
    mockAppScreen: {
      GB: {
        title: "Inbox",
        subtitle: "3 jobs in different stages",
        iconKey: "lightning",
        items: [
          { title: "Mr Wilson -- hallway + stairs", stage: "Quoted", color: "#F59E0B" },
          { title: "Garner family -- kitchen", stage: "On site", color: "#8B5CF6" },
          { title: "Flat 12, Park Court -- final coat", stage: "Finishing", color: "#10B981" },
        ],
      },
      US: {
        title: "Inbox",
        subtitle: "3 jobs in different stages",
        iconKey: "lightning",
        items: [
          { title: "Mr Wilson -- hallway + stairs", stage: "Quoted", color: "#F59E0B" },
          { title: "Garner family -- kitchen", stage: "On site", color: "#8B5CF6" },
          { title: "Apt 12, Park Court -- final coat", stage: "Finishing", color: "#10B981" },
        ],
      },
      CA: {
        title: "Inbox",
        subtitle: "3 jobs in different stages",
        iconKey: "lightning",
        items: [
          { title: "Mr Wilson -- hallway + stairs", stage: "Quoted", color: "#F59E0B" },
          { title: "Garner family -- kitchen", stage: "On site", color: "#8B5CF6" },
          { title: "Apt 12, Park Court -- final coat", stage: "Finishing", color: "#10B981" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Customers who haggle every line item, then leave a 1-star review when you won't repaint a wall for free.",
        solution:
          "The quote in writing, the variation in writing, the sign-off before you lift a brush.",
        solutionPunchline: "End of.",
      },
      {
        region: "US",
        problem:
          "Customers who haggle every line item, then leave a 1-star review when you won't repaint a wall for free.",
        solution:
          "The quote in writing, the change order in writing, the sign-off before you lift a brush.",
        solutionPunchline: "Done.",
      },
      {
        region: "CA",
        problem:
          "Customers who haggle every line item, then leave a 1-star review when you won't repaint a wall for free.",
        solution:
          "The quote in writing, the change order in writing, the sign-off before you lift a brush.",
        solutionPunchline: "Done.",
      },
    ],
    painPoints: {
      GB: [
        "Quoting a 3-day job that ends up taking 5 and losing money",
        "Forgetting which room has which paint brand or colour",
        "Invoicing that never quite matches what you actually did",
      ],
      US: [
        "Quoting a 3-day job that ends up taking 5 and losing money",
        "Forgetting which room has which paint brand or color",
        "Invoicing that never quite matches what you actually did",
      ],
      CA: [
        "Quoting a 3-day job that ends up taking 5 and losing money",
        "Forgetting which room has which paint brand or colour",
        "Invoicing that never quite matches what you actually did",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Room-by-room job tracking",
          body: "Break a house into rooms, tick them off as each is done. Customer sees progress on their share link.",
        },
        {
          title: "Paint codes stored per property",
          body: "Save the brand + code for each room. Next time they want a touch-up, you know exactly what to buy.",
        },
        {
          title: "Time logged against the job",
          body: "Start and stop on the phone. End of week, see hours-vs-quote on every job so you know which ones to re-price next time.",
        },
        {
          title: "Change-order quoting",
          body: "Customer wants a feature wall added mid-job? Send a short-form add-on quote in 30 seconds.",
        },
      ],
      US: [
        {
          title: "Room-by-room job tracking",
          body: "Break a house into rooms, check them off as each is done. Customer sees progress on their share link.",
        },
        {
          title: "Paint codes stored per property",
          body: "Save the brand + color code for each room. Next time they want a touch-up, you know exactly what to buy.",
        },
        {
          title: "Time logged against the job",
          body: "Start and stop on the phone. End of week, see hours-vs-quote on every job so you know which ones to re-price next time.",
        },
        {
          title: "Change-order quoting",
          body: "Customer wants an accent wall added mid-job? Send a short-form change order in 30 seconds.",
        },
      ],
      CA: [
        {
          title: "Room-by-room job tracking",
          body: "Break a house into rooms, check them off as each is done. Customer sees progress on their share link.",
        },
        {
          title: "Paint codes stored per property",
          body: "Save the brand + colour code for each room. Next time they want a touch-up, you know exactly what to buy.",
        },
        {
          title: "Time logged against the job",
          body: "Start and stop on the phone. End of week, see hours-vs-quote on every job so you know which ones to re-price next time.",
        },
        {
          title: "Change-order quoting",
          body: "Customer wants an accent wall added mid-job? Send a short-form change order in 30 seconds.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical whole-house job",
        steps: [
          "Quote: walk the house, capture each room with photos, Manos turns it into a multi-room quote.",
          "Start: log start time, pin paint codes as you go.",
          "Mid-job: customer adds another room. Send an add-on quote, they approve on their phone.",
          "End: mark complete, invoice reflects all rooms + changes, payment lands next day.",
        ],
      },
      US: {
        title: "A typical whole-house job",
        steps: [
          "Quote: walk the house, capture each room with photos, Manos turns it into a multi-room quote.",
          "Start: log start time, pin paint codes as you go.",
          "Mid-job: customer adds another room. Send a change order, they approve on their phone.",
          "End: mark complete, invoice reflects all rooms + changes, payment lands next day.",
        ],
      },
      CA: {
        title: "A typical whole-house job",
        steps: [
          "Quote: walk the house, capture each room with photos, Manos turns it into a multi-room quote.",
          "Start: log start time, pin paint codes as you go.",
          "Mid-job: customer adds another room. Send a change order, they approve on their phone.",
          "End: mark complete, invoice reflects all rooms + changes, payment lands next day.",
        ],
      },
    },
  },
  {
    slug: "locksmiths",
    name: "Locksmith",
    plural: "Locksmiths",
    accentColour: "amber",
    bookingHeroSlotLabel: "call-out",
    tapToPayTicketAmount: { GB: 150, US: 200, CA: 260 },
    tapToPayTicket: "£150.00",
    tapToPayLabel: "Call-out done -- tap to pay",
    heroHeadline: {
      GB: { intro: "Emergencies, scheduled jobs, insurance work.", punchline: "All on one phone." },
      US: { intro: "Emergencies, scheduled jobs, insurance work.", punchline: "All on one phone." },
      CA: { intro: "Emergencies, scheduled jobs, insurance work.", punchline: "All on one phone." },
    },
    heroSub: {
      GB: "Whether it's a 2am lockout or an insurance-required mortice upgrade, Manos keeps your callouts sorted and your paperwork straight.",
      US: "Whether it's a 2am lockout or an insurance-required deadbolt upgrade, Manos keeps your call-outs sorted and your paperwork straight.",
      CA: "Whether it's a 2am lockout or an insurance-required deadbolt upgrade, Manos keeps your call-outs sorted and your paperwork straight.",
    },
    mockAppScreen: {
      GB: {
        title: "Call-outs",
        subtitle: "2 emergency, 1 booked",
        iconKey: "lightning",
        items: [
          { title: "Margate Road -- locked out", stage: "Emergency", color: "#EF4444" },
          { title: "Heath Avenue -- snapped key", stage: "On the way", color: "#F59E0B" },
          { title: "Mr Davies -- new lock fit", stage: "Booked 2pm", color: "#3B82F6" },
        ],
      },
      US: {
        title: "Call-outs",
        subtitle: "2 emergency, 1 booked",
        iconKey: "lightning",
        items: [
          { title: "Margate Rd -- locked out", stage: "Emergency", color: "#EF4444" },
          { title: "Heath Ave -- broken key", stage: "On the way", color: "#F59E0B" },
          { title: "Mr Davies -- new lock install", stage: "Booked 2pm", color: "#3B82F6" },
        ],
      },
      CA: {
        title: "Call-outs",
        subtitle: "2 emergency, 1 booked",
        iconKey: "lightning",
        items: [
          { title: "Margate Rd -- locked out", stage: "Emergency", color: "#EF4444" },
          { title: "Heath Ave -- broken key", stage: "On the way", color: "#F59E0B" },
          { title: "Mr Davies -- new lock install", stage: "Booked 2pm", color: "#3B82F6" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "It's 2am, the customer's on the kerb, and you're doing maths in your head about callout fees.",
        solution:
          "Emergency rate locked in. Customer agrees on the doorstep, tap-to-pay before you leave.",
        solutionPunchline: "No awkward 'so about the bill' chat afterwards.",
      },
      {
        region: "US",
        problem:
          "It's 2am, the customer's on the curb, and you're doing math in your head about call-out fees.",
        solution:
          "Emergency rate locked in. Customer agrees on the doorstep, tap-to-pay before you leave.",
        solutionPunchline: "No awkward 'so about the bill' chat afterwards.",
      },
      {
        region: "CA",
        problem:
          "It's 2am, the customer's on the curb, and you're doing math in your head about call-out fees.",
        solution:
          "Emergency rate locked in. Customer agrees on the doorstep, tap-to-pay before you leave.",
        solutionPunchline: "No awkward 'so about the bill' chat afterwards.",
      },
    ],
    painPoints: {
      GB: [
        "Quoting over the phone at 11pm and forgetting the number by morning",
        "Insurance-compliant locks: tracking which spec each property needs",
        "Van stock not matching what the next job actually needs",
      ],
      US: [
        "Quoting over the phone at 11pm and forgetting the number by morning",
        "Insurance-compliant locks: tracking which grade each property needs",
        "Truck stock not matching what the next job actually needs",
      ],
      CA: [
        "Quoting over the phone at 11pm and forgetting the number by morning",
        "Insurance-compliant locks: tracking which grade each property needs",
        "Truck stock not matching what the next job actually needs",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Emergency call-out templates",
          body: "Send a quote via email in under 30 seconds. Customer approves, job lands on the schedule.",
        },
        {
          title: "Lock spec per property",
          body: "Tag each property with the insurance spec required (BS3621, TS007 3-star). When you're back for a repair, you already know what to fit.",
        },
        {
          title: "Van stock awareness",
          body: "Upcoming jobs show you what specs you'll need this week, so stock checks happen before the day of the job.",
        },
        {
          title: "Key-cutting records",
          body: "Track which customers have which key systems so you can re-cut or rekey quickly.",
        },
      ],
      US: [
        {
          title: "Emergency call-out templates",
          body: "Send a quote via email in under 30 seconds. Customer approves, job lands on the schedule.",
        },
        {
          title: "Lock grade per property",
          body: "Tag each property with the insurance grade required (ANSI Grade 1, Grade 2). When you're back for a repair, you already know what to install.",
        },
        {
          title: "Truck stock awareness",
          body: "Upcoming jobs show you what grades you'll need this week, so stock checks happen before the day of the job.",
        },
        {
          title: "Key-cutting records",
          body: "Track which customers have which key systems so you can re-cut or rekey quickly.",
        },
      ],
      CA: [
        {
          title: "Emergency call-out templates",
          body: "Send a quote via email in under 30 seconds. Customer approves, job lands on the schedule.",
        },
        {
          title: "Lock grade per property",
          body: "Tag each property with the insurance grade required (ANSI/CSA Grade 1, Grade 2). When you're back for a repair, you already know what to install.",
        },
        {
          title: "Truck stock awareness",
          body: "Upcoming jobs show you what grades you'll need this week, so stock checks happen before the day of the job.",
        },
        {
          title: "Key-cutting records",
          body: "Track which customers have which key systems so you can re-cut or rekey quickly.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical call-out",
        steps: [
          "2am phone call: voice-capture while you're driving, schedule entry created -- morning-of reminder fires automatically.",
          "On-site: quote using the emergency callout template, customer approves on their phone.",
          "Fit the lock, log the spec, invoice generated before you leave the driveway.",
          "Next morning: payment already landed.",
        ],
      },
      US: {
        title: "A typical call-out",
        steps: [
          "2am phone call: voice-capture while you're driving, schedule entry created -- morning-of reminder fires automatically.",
          "On-site: quote using the emergency call-out template, customer approves on their phone.",
          "Install the lock, log the grade, invoice generated before you leave the driveway.",
          "Next morning: payment already landed.",
        ],
      },
      CA: {
        title: "A typical call-out",
        steps: [
          "2am phone call: voice-capture while you're driving, schedule entry created -- morning-of reminder fires automatically.",
          "On-site: quote using the emergency call-out template, customer approves on their phone.",
          "Install the lock, log the grade, invoice generated before you leave the driveway.",
          "Next morning: payment already landed.",
        ],
      },
    },
  },
  {
    slug: "hair-stylists",
    name: "Hair stylist",
    plural: "Hair stylists",
    accentColour: "rose",
    bookingHeroSlotLabel: "appointment",
    tapToPayTicketAmount: { GB: 65, US: 95, CA: 120 },
    tapToPayTicket: "£65.00",
    tapToPayLabel: "Cut & blow-dry done",
    heroHeadline: {
      GB: { intro: "Six postcodes a day, repeat colour, late-cancel fees.", punchline: "All handled." },
      US: { intro: "Six neighborhoods a day, repeat color, late-cancel fees.", punchline: "All handled." },
      CA: { intro: "Six neighbourhoods a day, repeat colour, late-cancel fees.", punchline: "All handled." },
    },
    heroSub: {
      GB: "Mobile stylists running across town -- and chair-renters in a busy salon -- lean on Manos to track every customer's colour formula, book the next appointment as the customer leaves, and take a deposit before they can no-show.",
      US: "Mobile stylists running across town -- and booth renters in a busy salon -- lean on Manos to track every customer's color formula, book the next appointment as the customer leaves, and take a deposit before they can no-show.",
      CA: "Mobile stylists running across town -- and chair renters in a busy salon -- lean on Manos to track every customer's colour formula, book the next appointment as the customer leaves, and take a deposit before they can no-show.",
    },
    mockAppScreen: {
      GB: {
        title: "Today",
        subtitle: "6 appointments today",
        iconKey: "calendar",
        items: [
          { title: "9:30am -- Linda full colour", stage: "Confirmed", color: "#F43F5E" },
          { title: "11:00am -- Priya cut + blow dry", stage: "Confirmed", color: "#F43F5E" },
          { title: "2:00pm -- James shape-up", stage: "New booking", color: "#F59E0B" },
        ],
      },
      US: {
        title: "Today",
        subtitle: "6 appointments today",
        iconKey: "calendar",
        items: [
          { title: "9:30am -- Linda full color", stage: "Confirmed", color: "#F43F5E" },
          { title: "11:00am -- Priya cut + blowout", stage: "Confirmed", color: "#F43F5E" },
          { title: "2:00pm -- James shape-up", stage: "New booking", color: "#F59E0B" },
        ],
      },
      CA: {
        title: "Today",
        subtitle: "6 appointments today",
        iconKey: "calendar",
        items: [
          { title: "9:30am -- Linda full colour", stage: "Confirmed", color: "#F43F5E" },
          { title: "11:00am -- Priya cut + blowout", stage: "Confirmed", color: "#F43F5E" },
          { title: "2:00pm -- James shape-up", stage: "New booking", color: "#F59E0B" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Halfway to the next house when the client texts to cancel. The petrol's gone, the slot's empty, and you've got nothing booked behind it.",
        solution:
          "Deposits taken at booking. The slot opens back up to your booking page so you can refill it.",
        solutionPunchline: "No more driving for nothing.",
      },
      {
        region: "US",
        problem:
          "Halfway to the next house when the client texts to cancel. The gas is gone, the slot's empty, and you've got nothing booked behind it.",
        solution:
          "Deposits taken at booking. The slot opens back up to your booking page so you can refill it.",
        solutionPunchline: "No more driving for nothing.",
      },
      {
        region: "CA",
        problem:
          "Halfway to the next house when the client texts to cancel. The gas is gone, the slot's empty, and you've got nothing booked behind it.",
        solution:
          "Deposits taken at booking. The slot opens back up to your booking page so you can refill it.",
        solutionPunchline: "No more driving for nothing.",
      },
    ],
    painPoints: {
      GB: [
        "Driving across town when the postcodes don't line up",
        "Losing the colour formula the second time they book",
        "Customers wanting to book at odd hours when you're driving",
      ],
      US: [
        "Driving across town when the ZIP codes don't line up",
        "Losing the color formula the second time they book",
        "Customers wanting to book at odd hours when you're driving",
      ],
      CA: [
        "Driving across town when the postal codes don't line up",
        "Losing the colour formula the second time they book",
        "Customers wanting to book at odd hours when you're driving",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Colour formulas per customer",
          body: "Last bleach wash, toner, developer -- all saved. Next colour session, you know exactly what worked and what didn't.",
        },
        {
          title: "Recurring appointment booking",
          body: "Book the next cut before they leave the chair. Reminders send 48 hours before so no-shows become rebooks.",
        },
        {
          title: "Deposit & late-cancel fees",
          body: "Customers pay a deposit at booking. The stylist keeps it on a no-show, no awkward conversation needed.",
        },
        {
          title: "Online booking link",
          body: "Send a Calendly-style link and let customers book inside your available hours -- or let the whole world book via your public profile.",
        },
      ],
      US: [
        {
          title: "Color formulas per customer",
          body: "Last bleach wash, toner, developer -- all saved. Next color session, you know exactly what worked and what didn't.",
        },
        {
          title: "Recurring appointment booking",
          body: "Book the next cut before they leave the chair. Reminders send 48 hours before so no-shows become rebooks.",
        },
        {
          title: "Deposit & late-cancel fees",
          body: "Customers pay a deposit at booking. The stylist keeps it on a no-show, no awkward conversation needed.",
        },
        {
          title: "Online booking link",
          body: "Send a Calendly-style link and let customers book inside your available hours -- or let the whole world book via your public profile.",
        },
      ],
      CA: [
        {
          title: "Colour formulas per customer",
          body: "Last bleach wash, toner, developer -- all saved. Next colour session, you know exactly what worked and what didn't.",
        },
        {
          title: "Recurring appointment booking",
          body: "Book the next cut before they leave the chair. Reminders send 48 hours before so no-shows become rebooks.",
        },
        {
          title: "Deposit & late-cancel fees",
          body: "Customers pay a deposit at booking. The stylist keeps it on a no-show, no awkward conversation needed.",
        },
        {
          title: "Online booking link",
          body: "Send a Calendly-style link and let customers book inside your available hours -- or let the whole world book via your public profile.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical appointment",
        steps: [
          "Customer walks in, you check their record: last colour, last cut date, any notes.",
          "During: update the colour formula as you mix, add any retail products to the bill.",
          "Checkout: one tap to invoice; tap-to-pay if the customer wants card. Next appointment booked before they leave.",
          "48 hours before next visit: they get an automatic reminder.",
        ],
      },
      US: {
        title: "A typical appointment",
        steps: [
          "Customer walks in, you check their record: last color, last cut date, any notes.",
          "During: update the color formula as you mix, add any retail products to the bill.",
          "Checkout: one tap to invoice; tap-to-pay if the customer wants card. Next appointment booked before they leave.",
          "48 hours before next visit: they get an automatic reminder.",
        ],
      },
      CA: {
        title: "A typical appointment",
        steps: [
          "Customer walks in, you check their record: last colour, last cut date, any notes.",
          "During: update the colour formula as you mix, add any retail products to the bill.",
          "Checkout: one tap to invoice; tap-to-pay if the customer wants card. Next appointment booked before they leave.",
          "48 hours before next visit: they get an automatic reminder.",
        ],
      },
    },
  },
  // Personal trainers removed 2026-04-26: Benchmark Plus is the
  // dedicated trainer-CRM tier in the KH portfolio, so pitching Manos
  // to PTs cannibalised the sister product. Trainers landing on
  // Manos's marketing should be routed to Benchmark instead. (Issue
  // for the routing nudge: TBD when the homepage adds a sister-
  // products band.)
  {
    // Not covered by the #129 doc. Existing GB-only copy preserved
    // and duplicated to US + CA so the resolver finds an entry for
    // every visitor; doc-driven US/CA variants are a follow-up
    // ticket if/when this trade gets traction outside the UK.
    slug: "dog-groomers",
    name: "Dog groomer",
    plural: "Dog groomers",
    accentColour: "teal",
    bookingHeroSlotLabel: "groom",
    tapToPayTicketAmount: { GB: 55, US: 75, CA: 90 },
    tapToPayTicket: "£55.00",
    tapToPayLabel: "Groom complete",
    heroHeadline: {
      GB: { intro: "Breed profiles, repeat grooms,", punchline: "and happy-to-pay customers." },
      US: { intro: "Breed profiles, repeat grooms,", punchline: "and happy-to-pay customers." },
      CA: { intro: "Breed profiles, repeat grooms,", punchline: "and happy-to-pay customers." },
    },
    heroSub: {
      GB: "Every dog you groom has preferences, anxieties, and a coat that grows at its own pace. Manos remembers them so you don't have to.",
      US: "Every dog you groom has preferences, anxieties, and a coat that grows at its own pace. Manos remembers them so you don't have to.",
      CA: "Every dog you groom has preferences, anxieties, and a coat that grows at its own pace. Manos remembers them so you don't have to.",
    },
    mockAppScreen: {
      GB: {
        title: "Today",
        subtitle: "3 in the salon today",
        iconKey: "calendar",
        items: [
          { title: "10:00am -- Bella, Cocker Spaniel", stage: "Full groom", color: "#14B8A6" },
          { title: "12:00pm -- Max, Border Collie", stage: "De-shed + bath", color: "#3B82F6" },
          { title: "3:00pm -- Luna, Standard Poodle", stage: "Show clip", color: "#F59E0B" },
        ],
      },
      US: {
        title: "Today",
        subtitle: "3 in the salon today",
        iconKey: "calendar",
        items: [
          { title: "10:00am -- Bella, Cocker Spaniel", stage: "Full groom", color: "#14B8A6" },
          { title: "12:00pm -- Max, Border Collie", stage: "De-shed + bath", color: "#3B82F6" },
          { title: "3:00pm -- Luna, Standard Poodle", stage: "Show clip", color: "#F59E0B" },
        ],
      },
      CA: {
        title: "Today",
        subtitle: "3 in the salon today",
        iconKey: "calendar",
        items: [
          { title: "10:00am -- Bella, Cocker Spaniel", stage: "Full groom", color: "#14B8A6" },
          { title: "12:00pm -- Max, Border Collie", stage: "De-shed + bath", color: "#3B82F6" },
          { title: "3:00pm -- Luna, Standard Poodle", stage: "Show clip", color: "#F59E0B" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Customers used to think a deposit was optional. One too many no-shows blowing up the afternoon.",
        solution: "Booking, deposit, email reminders -- all sorted.",
        solutionPunchline: "No 7pm WhatsApp chase.",
      },
      {
        region: "US",
        problem:
          "Customers used to think a deposit was optional. One too many no-shows blowing up the afternoon.",
        solution: "Booking, deposit, email reminders -- all sorted.",
        solutionPunchline: "No 7pm WhatsApp chase.",
      },
      {
        region: "CA",
        problem:
          "Customers used to think a deposit was optional. One too many no-shows blowing up the afternoon.",
        solution: "Booking, deposit, email reminders -- all sorted.",
        solutionPunchline: "No 7pm WhatsApp chase.",
      },
    ],
    painPoints: {
      GB: [
        "Remembering whether Bruno is nervous about the dryer or not",
        "Pricing a cocker spaniel the same as a wolfhound",
        "Invoicing and card readers in a small mobile van",
      ],
      US: [
        "Remembering whether Bruno is nervous about the dryer or not",
        "Pricing a cocker spaniel the same as a wolfhound",
        "Invoicing and card readers in a small mobile van",
      ],
      CA: [
        "Remembering whether Bruno is nervous about the dryer or not",
        "Pricing a cocker spaniel the same as a wolfhound",
        "Invoicing and card readers in a small mobile van",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Per-dog profiles",
          body: "Breed, coat type, last groom date, sensitivities (grooms too hot? scared of clippers?) -- all on one screen.",
        },
        {
          title: "Rebook-on-the-6-week-cycle",
          body: "Most breeds need a groom on a schedule. Mark a groom done and Manos pings you to rebook the next one at the right time.",
        },
        {
          title: "Breed-based pricing",
          body: "Default prices by breed mean no more 'how much?' calls before appointments.",
        },
        {
          title: "Mobile-first checkout",
          body: "Tap-to-pay at the end of the groom. No card reader to buy, just your phone.",
        },
      ],
      US: [
        {
          title: "Per-dog profiles",
          body: "Breed, coat type, last groom date, sensitivities (grooms too hot? scared of clippers?) -- all on one screen.",
        },
        {
          title: "Rebook-on-the-6-week-cycle",
          body: "Most breeds need a groom on a schedule. Mark a groom done and Manos pings you to rebook the next one at the right time.",
        },
        {
          title: "Breed-based pricing",
          body: "Default prices by breed mean no more 'how much?' calls before appointments.",
        },
        {
          title: "Mobile-first checkout",
          body: "Tap-to-pay at the end of the groom. No card reader to buy, just your phone.",
        },
      ],
      CA: [
        {
          title: "Per-dog profiles",
          body: "Breed, coat type, last groom date, sensitivities (grooms too hot? scared of clippers?) -- all on one screen.",
        },
        {
          title: "Rebook-on-the-6-week-cycle",
          body: "Most breeds need a groom on a schedule. Mark a groom done and Manos pings you to rebook the next one at the right time.",
        },
        {
          title: "Breed-based pricing",
          body: "Default prices by breed mean no more 'how much?' calls before appointments.",
        },
        {
          title: "Mobile-first checkout",
          body: "Tap-to-pay at the end of the groom. No card reader to buy, just your phone.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical groom",
        steps: [
          "Dog arrives: check their profile -- last groom 6 weeks ago, cocker spaniel, dislikes the dryer.",
          "Groom to pattern, update notes as you go (used a calming spray, they settled).",
          "Mark complete; tap-to-pay or invoice. Owner gets a quick photo if you've snapped one.",
          "Reminder set for 6 weeks' time so you can rebook before the next groom is due.",
        ],
      },
      US: {
        title: "A typical groom",
        steps: [
          "Dog arrives: check their profile -- last groom 6 weeks ago, cocker spaniel, dislikes the dryer.",
          "Groom to pattern, update notes as you go (used a calming spray, they settled).",
          "Mark complete; tap-to-pay or invoice. Owner gets a quick photo if you've snapped one.",
          "Reminder set for 6 weeks' time so you can rebook before the next groom is due.",
        ],
      },
      CA: {
        title: "A typical groom",
        steps: [
          "Dog arrives: check their profile -- last groom 6 weeks ago, cocker spaniel, dislikes the dryer.",
          "Groom to pattern, update notes as you go (used a calming spray, they settled).",
          "Mark complete; tap-to-pay or invoice. Owner gets a quick photo if you've snapped one.",
          "Reminder set for 6 weeks' time so you can rebook before the next groom is due.",
        ],
      },
    },
  },
  {
    slug: "tutors",
    name: "Tutor",
    plural: "Tutors",
    accentColour: "blue",
    bookingHeroSlotLabel: "lesson",
    tapToPayTicketAmount: { GB: 30, US: 50, CA: 60 },
    tapToPayTicket: "£30.00",
    tapToPayLabel: "Lesson finished",
    heroHeadline: {
      GB: { intro: "Lesson plans, recurring sessions,", punchline: "invoiced weekly." },
      US: { intro: "Lesson plans, recurring sessions,", punchline: "invoiced weekly." },
      CA: { intro: "Lesson plans, recurring sessions,", punchline: "invoiced weekly." },
    },
    heroSub: {
      GB: "Whether you're an in-person maths tutor or an online language specialist, Manos tracks every lesson, logs parents' contact details, and invoices on the schedule you set.",
      US: "Whether you're an in-person math tutor or an online language specialist, Manos tracks every lesson, logs parents' contact details, and invoices on the schedule you set.",
      CA: "Whether you're an in-person math tutor or an online language specialist, Manos tracks every lesson, logs parents' contact details, and invoices on the schedule you set.",
    },
    mockAppScreen: {
      GB: {
        title: "This week",
        subtitle: "6 sessions to teach",
        iconKey: "calendar",
        items: [
          { title: "Mon 4pm -- Emma, Maths", stage: "Weekly", color: "#3B82F6" },
          { title: "Tue 6pm -- Joel, Physics", stage: "Block paid", color: "#10B981" },
          { title: "Wed 5pm -- Mei, English", stage: "Trial", color: "#F59E0B" },
        ],
      },
      US: {
        title: "This week",
        subtitle: "6 sessions to teach",
        iconKey: "calendar",
        items: [
          { title: "Mon 4pm -- Emma, Math", stage: "Weekly", color: "#3B82F6" },
          { title: "Tue 6pm -- Joel, Physics", stage: "Block paid", color: "#10B981" },
          { title: "Wed 5pm -- Mei, English", stage: "Trial", color: "#F59E0B" },
        ],
      },
      CA: {
        title: "This week",
        subtitle: "6 sessions to teach",
        iconKey: "calendar",
        items: [
          { title: "Mon 4pm -- Emma, Math", stage: "Weekly", color: "#3B82F6" },
          { title: "Tue 6pm -- Joel, Physics", stage: "Block paid", color: "#10B981" },
          { title: "Wed 5pm -- Mei, English", stage: "Trial", color: "#F59E0B" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Parents would book a slot, never confirm payment, then no-show. By the third one I'd lost a Saturday morning for nothing.",
        solution:
          "Manos handles the booking, takes the deposit, reminds them automatically.",
        solutionPunchline: "Just turn up and teach.",
      },
      {
        region: "US",
        problem:
          "Parents would book a slot, never confirm payment, then no-show. By the third one I'd lost a Saturday morning for nothing.",
        solution:
          "Manos handles the booking, takes the deposit, reminds them automatically.",
        solutionPunchline: "Just turn up and teach.",
      },
      {
        region: "CA",
        problem:
          "Parents would book a slot, never confirm payment, then no-show. By the third one I'd lost a Saturday morning for nothing.",
        solution:
          "Manos handles the booking, takes the deposit, reminds them automatically.",
        solutionPunchline: "Just turn up and teach.",
      },
    ],
    painPoints: {
      GB: [
        "Remembering which student is on which syllabus",
        "Weekly recurring lessons that blur into one big schedule",
        "Online vs in-person pricing differences",
      ],
      US: [
        "Remembering which student is on which curriculum",
        "Weekly recurring lessons that blur into one big schedule",
        "Online vs in-person pricing differences",
      ],
      CA: [
        "Remembering which student is on which curriculum",
        "Weekly recurring lessons that blur into one big schedule",
        "Online vs in-person pricing differences",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Per-student profiles",
          body: "Syllabus, exam board, weak topics, last session notes -- all saved against the student.",
        },
        {
          title: "Recurring weekly lessons",
          body: "Book a recurring slot for the term.",
        },
        {
          title: "Invoice monthly when you're ready",
          body: "Tally lessons through the month and send the invoice when you're ready.",
        },
        {
          title: "Different rates per subject",
          body: "Maths at one rate, language prep at another, exam-season intensive at a third -- Manos picks the right price per lesson.",
        },
      ],
      US: [
        {
          title: "Per-student profiles",
          body: "Curriculum, grade level, weak topics, last session notes -- all saved against the student.",
        },
        {
          title: "Recurring weekly lessons",
          body: "Book a recurring slot for the semester.",
        },
        {
          title: "Invoice monthly when you're ready",
          body: "Tally lessons through the month and send the invoice when you're ready.",
        },
        {
          title: "Different rates per subject",
          body: "Math at one rate, AP/SAT prep at another, language at a third -- Manos picks the right price per lesson.",
        },
      ],
      CA: [
        {
          title: "Per-student profiles",
          body: "Curriculum, grade level, weak topics, last session notes -- all saved against the student.",
        },
        {
          title: "Recurring weekly lessons",
          body: "Book a recurring slot for the term.",
        },
        {
          title: "Invoice monthly when you're ready",
          body: "Tally lessons through the month and send the invoice when you're ready.",
        },
        {
          title: "Different rates per subject",
          body: "Math at one rate, AP/IB prep at another, language at a third -- Manos picks the right price per lesson.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical term",
        steps: [
          "Sign-up: parent books a weekly Tuesday slot for the term.",
          "Each lesson: log notes, mark complete, student profile updates with what you covered.",
          "End of month: tally the lessons and send the invoice manually when you're ready.",
        ],
      },
      US: {
        title: "A typical term",
        steps: [
          "Sign-up: parent books a weekly Tuesday slot for the semester.",
          "Each lesson: log notes, mark complete, student profile updates with what you covered.",
          "End of month: tally the lessons and send the invoice manually when you're ready.",
        ],
      },
      CA: {
        title: "A typical term",
        steps: [
          "Sign-up: parent books a weekly Tuesday slot for the term.",
          "Each lesson: log notes, mark complete, student profile updates with what you covered.",
          "End of month: tally the lessons and send the invoice manually when you're ready.",
        ],
      },
    },
  },
  {
    // B2B specialist booking. Same customer-led shape as tutors but
    // skewed to professional services -- business / marketing / HR /
    // fractional-CXO consultants. Mainly online; high hourly rate;
    // long retainers common. Teal accent because no other trade uses
    // it -- signals a more professional / studio / advisory feel
    // versus the warm trade colours.
    slug: "consultants",
    name: "Consultant",
    plural: "Consultants",
    accentColour: "teal",
    bookingHeroSlotLabel: "session",
    tapToPayTicketAmount: { GB: 250, US: 350, CA: 450 },
    tapToPayTicket: "£250.00",
    tapToPayLabel: "Session ended",
    heroHeadline: {
      GB: { intro: "Specialist sessions, clean billing,", punchline: "no admin tail." },
      US: { intro: "Specialist sessions, clean billing,", punchline: "no admin tail." },
      CA: { intro: "Specialist sessions, clean billing,", punchline: "no admin tail." },
    },
    heroSub: {
      GB: "Whether you're a business strategist, a marketing consultant, an HR advisor, or a fractional CFO -- Manos handles the bookings, the notes, and the invoices, so the only thing you sell is your hour.",
      US: "Whether you're a business strategist, a marketing consultant, an HR advisor, or a fractional CFO -- Manos handles the bookings, the notes, and the invoices, so the only thing you sell is your hour.",
      CA: "Whether you're a business strategist, a marketing consultant, an HR advisor, or a fractional CFO -- Manos handles the bookings, the notes, and the invoices, so the only thing you sell is your hour.",
    },
    mockAppScreen: {
      GB: {
        title: "This week",
        subtitle: "5 sessions on the books",
        iconKey: "calendar",
        items: [
          { title: "Mon 10am -- Acme, strategy review", stage: "Retainer", color: "#14B8A6" },
          { title: "Tue 2pm -- Belle, board prep", stage: "One-off", color: "#3B82F6" },
          { title: "Thu 9am -- Northwind, intro call", stage: "Discovery", color: "#F59E0B" },
        ],
      },
      US: {
        title: "This week",
        subtitle: "5 sessions on the books",
        iconKey: "calendar",
        items: [
          { title: "Mon 10am -- Acme, strategy review", stage: "Retainer", color: "#14B8A6" },
          { title: "Tue 2pm -- Belle, board prep", stage: "One-off", color: "#3B82F6" },
          { title: "Thu 9am -- Northwind, intro call", stage: "Discovery", color: "#F59E0B" },
        ],
      },
      CA: {
        title: "This week",
        subtitle: "5 sessions on the books",
        iconKey: "calendar",
        items: [
          { title: "Mon 10am -- Acme, strategy review", stage: "Retainer", color: "#14B8A6" },
          { title: "Tue 2pm -- Belle, board prep", stage: "One-off", color: "#3B82F6" },
          { title: "Thu 9am -- Northwind, intro call", stage: "Discovery", color: "#F59E0B" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Half my Friday afternoon is invoicing -- working out which clients had how many sessions, who's on retainer, who I'd offered a discount to and what for.",
        solution:
          "Manos tracks every session as you book it, then drafts the invoice when the month closes.",
        solutionPunchline: "Bill in two clicks, not two hours.",
      },
      {
        region: "US",
        problem:
          "Half my Friday afternoon is invoicing -- working out which clients had how many sessions, who's on retainer, who I'd offered a discount to and what for.",
        solution:
          "Manos tracks every session as you book it, then drafts the invoice when the month closes.",
        solutionPunchline: "Bill in two clicks, not two hours.",
      },
      {
        region: "CA",
        problem:
          "Half my Friday afternoon is invoicing -- working out which clients had how many sessions, who's on retainer, who I'd offered a discount to and what for.",
        solution:
          "Manos tracks every session as you book it, then drafts the invoice when the month closes.",
        solutionPunchline: "Bill in two clicks, not two hours.",
      },
    ],
    painPoints: {
      GB: [
        "Repeat clients on retainer mixed with one-off discovery calls",
        "Different rates for strategy, workshops, and on-call hours",
        "Calendar tools that don't know what each client is paying for",
      ],
      US: [
        "Repeat clients on retainer mixed with one-off discovery calls",
        "Different rates for strategy, workshops, and on-call hours",
        "Calendar tools that don't know what each client is paying for",
      ],
      CA: [
        "Repeat clients on retainer mixed with one-off discovery calls",
        "Different rates for strategy, workshops, and on-call hours",
        "Calendar tools that don't know what each client is paying for",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Per-client engagement profiles",
          body: "Engagement type, retainer terms, last session notes, deliverables outstanding -- all saved against the client.",
        },
        {
          title: "Retainer + one-off in one schedule",
          body: "Recurring retainer slots and ad-hoc discovery calls share the same calendar; Manos knows which is which when invoicing.",
        },
        {
          title: "Different rates per service",
          body: "Strategy at one rate, workshops at another, on-call hours at a third -- pick the right service when you book the slot, the price comes with it.",
        },
        {
          title: "Invoice when the month closes",
          body: "Tally the month's sessions per client, send the invoice with the breakdown attached. Stripe, bank transfer, or both.",
        },
      ],
      US: [
        {
          title: "Per-client engagement profiles",
          body: "Engagement type, retainer terms, last session notes, deliverables outstanding -- all saved against the client.",
        },
        {
          title: "Retainer + one-off in one schedule",
          body: "Recurring retainer slots and ad-hoc discovery calls share the same calendar; Manos knows which is which when invoicing.",
        },
        {
          title: "Different rates per service",
          body: "Strategy at one rate, workshops at another, on-call hours at a third -- pick the right service when you book the slot, the price comes with it.",
        },
        {
          title: "Invoice when the month closes",
          body: "Tally the month's sessions per client, send the invoice with the breakdown attached. Stripe, bank transfer, or both.",
        },
      ],
      CA: [
        {
          title: "Per-client engagement profiles",
          body: "Engagement type, retainer terms, last session notes, deliverables outstanding -- all saved against the client.",
        },
        {
          title: "Retainer + one-off in one schedule",
          body: "Recurring retainer slots and ad-hoc discovery calls share the same calendar; Manos knows which is which when invoicing.",
        },
        {
          title: "Different rates per service",
          body: "Strategy at one rate, workshops at another, on-call hours at a third -- pick the right service when you book the slot, the price comes with it.",
        },
        {
          title: "Invoice when the month closes",
          body: "Tally the month's sessions per client, send the invoice with the breakdown attached. Stripe, bank transfer, or both.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical month",
        steps: [
          "Discovery call: client books a 30-minute slot from your link, gets a confirmation, lands on your calendar.",
          "Engaged: book a recurring retainer slot or ad-hoc sessions; log notes and deliverables after each.",
          "Month-end: tally the month's sessions per client and send the invoice in one tap.",
        ],
      },
      US: {
        title: "A typical month",
        steps: [
          "Discovery call: client books a 30-minute slot from your link, gets a confirmation, lands on your calendar.",
          "Engaged: book a recurring retainer slot or ad-hoc sessions; log notes and deliverables after each.",
          "Month-end: tally the month's sessions per client and send the invoice in one tap.",
        ],
      },
      CA: {
        title: "A typical month",
        steps: [
          "Discovery call: client books a 30-minute slot from your link, gets a confirmation, lands on your calendar.",
          "Engaged: book a recurring retainer slot or ad-hoc sessions; log notes and deliverables after each.",
          "Month-end: tally the month's sessions per client and send the invoice in one tap.",
        ],
      },
    },
  },
  {
    // Catch-all entry for any service-business owner who doesn't fit
    // one of the 12 specific trade pages. Linked from the "Your trade"
    // nav dropdown ("All trades") and from the bottom of every other
    // trade's "Also using Manos" footer when the visitor's trade isn't
    // covered. Manos amber accent so it reads as the brand-default,
    // not a separate trade hue.
    //
    // Not covered by the #129 doc. Existing GB-only copy preserved
    // and duplicated to US + CA so the resolver finds an entry for
    // every visitor; doc-driven US/CA variants are a follow-up
    // ticket if/when the catch-all picks up traction outside the UK.
    slug: "all-trades",
    name: "Service business",
    plural: "All trades",
    accentColour: "amber",
    bookingHeroSlotLabel: "appointment",
    tapToPayTicketAmount: { GB: 100, US: 150, CA: 180 },
    tapToPayTicket: "£100.00",
    tapToPayLabel: "Job done -- tap to pay",
    heroHeadline: {
      GB: { intro: "Run any trade, any service business.", punchline: "Without the spreadsheet sprawl." },
      US: { intro: "Run any trade, any service business.", punchline: "Without the spreadsheet sprawl." },
      CA: { intro: "Run any trade, any service business.", punchline: "Without the spreadsheet sprawl." },
    },
    heroSub: {
      GB: "If your week ends with a kitchen-table session sorting invoices, tracking customers, and chasing the ones who went quiet -- Manos is the one place to put it. Built for any trade or service business that bills by the job.",
      US: "If your week ends with a kitchen-table session sorting invoices, tracking customers, and chasing the ones who went quiet -- Manos is the one place to put it. Built for any trade or service business that bills by the job.",
      CA: "If your week ends with a kitchen-table session sorting invoices, tracking customers, and chasing the ones who went quiet -- Manos is the one place to put it. Built for any trade or service business that bills by the job.",
    },
    mockAppScreen: {
      GB: {
        title: "Inbox",
        subtitle: "3 items need attention",
        iconKey: "lightning",
        items: [
          { title: "Mrs Patel -- new enquiry", stage: "Enquiry", color: "#6B7280" },
          { title: "Mr Khan -- quote sent", stage: "Quoted", color: "#F59E0B" },
          { title: "Sarah Chen -- scheduled today", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
      US: {
        title: "Inbox",
        subtitle: "3 items need attention",
        iconKey: "lightning",
        items: [
          { title: "Mrs Patel -- new inquiry", stage: "Inquiry", color: "#6B7280" },
          { title: "Mr Khan -- quote sent", stage: "Quoted", color: "#F59E0B" },
          { title: "Sarah Chen -- scheduled today", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
      CA: {
        title: "Inbox",
        subtitle: "3 items need attention",
        iconKey: "lightning",
        items: [
          { title: "Mrs Patel -- new inquiry", stage: "Inquiry", color: "#6B7280" },
          { title: "Mr Khan -- quote sent", stage: "Quoted", color: "#F59E0B" },
          { title: "Sarah Chen -- scheduled today", stage: "Scheduled", color: "#3B82F6" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Sunday night is for chasing invoices, sorting receipts, and trying to remember who I promised what.",
        solution:
          "Manos does the chasing, sorts the receipts, remembers what you promised.",
        solutionPunchline: "Get your Sunday back.",
      },
      {
        region: "US",
        problem:
          "Sunday night is for chasing invoices, sorting receipts, and trying to remember who I promised what.",
        solution:
          "Manos does the chasing, sorts the receipts, remembers what you promised.",
        solutionPunchline: "Get your Sunday back.",
      },
      {
        region: "CA",
        problem:
          "Sunday night is for chasing invoices, sorting receipts, and trying to remember who I promised what.",
        solution:
          "Manos does the chasing, sorts the receipts, remembers what you promised.",
        solutionPunchline: "Get your Sunday back.",
      },
    ],
    painPoints: {
      GB: [
        "Customers who go quiet between the quote and the deposit",
        "Receipts that vanish before they hit the books",
        "Following up so often it starts to feel rude",
      ],
      US: [
        "Customers who go quiet between the quote and the deposit",
        "Receipts that vanish before they hit the books",
        "Following up so often it starts to feel rude",
      ],
      CA: [
        "Customers who go quiet between the quote and the deposit",
        "Receipts that vanish before they hit the books",
        "Following up so often it starts to feel rude",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Voice-capture jobs as they happen",
          body: "Dictate the next job between visits. 'Mrs Patel, fix the thing, Tuesday morning' becomes a scheduled appointment with the customer attached.",
        },
        {
          title: "Quote templates for the work you do most",
          body: "Save the quotes you send a hundred times. One tap to apply, five seconds to customise per customer.",
        },
        {
          title: "Reminders before the panic",
          body: "Recurring jobs, customer renewals, and follow-ups all surface in your dashboard before they slip. Quiet weeks fill themselves.",
        },
        {
          title: "One-tap invoicing + tap-to-pay",
          body: "Mark a job complete, Manos drafts the invoice. Take payment on the doorstep with your phone, or send it for bank transfer. All reconciled in your books.",
        },
      ],
      US: [
        {
          title: "Voice-capture jobs as they happen",
          body: "Dictate the next job between visits. 'Mrs Patel, fix the thing, Tuesday morning' becomes a scheduled appointment with the customer attached.",
        },
        {
          title: "Quote templates for the work you do most",
          body: "Save the quotes you send a hundred times. One tap to apply, five seconds to customize per customer.",
        },
        {
          title: "Reminders before the panic",
          body: "Recurring jobs, customer renewals, and follow-ups all surface in your dashboard before they slip. Quiet weeks fill themselves.",
        },
        {
          title: "One-tap invoicing + tap-to-pay",
          body: "Mark a job complete, Manos drafts the invoice. Take payment on the doorstep with your phone, or send it for bank transfer. All reconciled in your books.",
        },
      ],
      CA: [
        {
          title: "Voice-capture jobs as they happen",
          body: "Dictate the next job between visits. 'Mrs Patel, fix the thing, Tuesday morning' becomes a scheduled appointment with the customer attached.",
        },
        {
          title: "Quote templates for the work you do most",
          body: "Save the quotes you send a hundred times. One tap to apply, five seconds to customize per customer.",
        },
        {
          title: "Reminders before the panic",
          body: "Recurring jobs, customer renewals, and follow-ups all surface in your dashboard before they slip. Quiet weeks fill themselves.",
        },
        {
          title: "One-tap invoicing + tap-to-pay",
          body: "Mark a job complete, Manos drafts the invoice. Take payment on the doorstep with your phone, or send it for bank transfer. All reconciled in your books.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical week",
        steps: [
          "Monday morning: today's jobs are on the schedule, this week's enquiries are in the inbox.",
          "On-site: quote, mark a job complete, take payment -- all from your phone, all in seconds.",
          "Evening: invoices auto-draft as you complete jobs. Send when you're ready.",
          "Sunday: the dashboard tells you the week was good. No spreadsheet, no panic.",
        ],
      },
      US: {
        title: "A typical week",
        steps: [
          "Monday morning: today's jobs are on the schedule, this week's inquiries are in the inbox.",
          "On-site: quote, mark a job complete, take payment -- all from your phone, all in seconds.",
          "Evening: invoices auto-draft as you complete jobs. Send when you're ready.",
          "Sunday: the dashboard tells you the week was good. No spreadsheet, no panic.",
        ],
      },
      CA: {
        title: "A typical week",
        steps: [
          "Monday morning: today's jobs are on the schedule, this week's inquiries are in the inbox.",
          "On-site: quote, mark a job complete, take payment -- all from your phone, all in seconds.",
          "Evening: invoices auto-draft as you complete jobs. Send when you're ready.",
          "Sunday: the dashboard tells you the week was good. No spreadsheet, no panic.",
        ],
      },
    },
  },
  // [research needed] State-inspection requirements vary widely across
  // the US -- 16 states require none, others require annually, others
  // biennially. Generic copy uses "state inspection" / "inspection"
  // which works universally. CA is more uniform per province but still
  // varies (Ontario: Safety Standards Certificate; BC: passenger
  // vehicle inspection; Quebec: SAAQ). A licensed mobile-mechanic
  // should sanity-check these before launch.
  {
    slug: "mobile-mechanics",
    name: "Mobile mechanic",
    plural: "Mobile mechanics",
    accentColour: "orange",
    bookingHeroSlotLabel: "call-out",
    tapToPayTicketAmount: { GB: 140, US: 200, CA: 250 },
    tapToPayTicket: "£140.00",
    tapToPayLabel: "Job done -- tap to pay",
    heroHeadline: {
      GB: { intro: "Roadside calls, driveway services, MOT-prep checks.", punchline: "All in one calendar." },
      US: { intro: "Roadside calls, driveway services, state-inspection prep.", punchline: "All in one calendar." },
      CA: { intro: "Roadside calls, driveway services, safety-inspection prep.", punchline: "All in one calendar." },
    },
    heroSub: {
      GB: "Whether it's a battery jump in a Tesco car park or a brake job on someone's drive, Manos keeps your callouts sorted, your parts list straight, and your invoicing one tap away from done.",
      US: "Whether it's a battery jump in a parking lot or a brake job on someone's driveway, Manos keeps your call-outs sorted, your parts list straight, and your invoicing one tap away from done.",
      CA: "Whether it's a battery jump in a parking lot or a brake job on someone's driveway, Manos keeps your call-outs sorted, your parts list straight, and your invoicing one tap away from done.",
    },
    mockAppScreen: {
      GB: {
        title: "Today's call-outs",
        subtitle: "1 emergency, 2 booked",
        iconKey: "lightning",
        items: [
          { title: "Mr Davies -- battery callout", stage: "Emergency", color: "#EF4444" },
          { title: "Mrs Patel driveway -- brake job", stage: "On site", color: "#F97316" },
          { title: "Henderson -- pre-MOT check", stage: "Booked Sat", color: "#3B82F6" },
        ],
      },
      US: {
        title: "Today's call-outs",
        subtitle: "1 emergency, 2 booked",
        iconKey: "lightning",
        items: [
          { title: "Mr Davies -- battery jump", stage: "Emergency", color: "#EF4444" },
          { title: "Mrs Patel driveway -- brake job", stage: "On site", color: "#F97316" },
          { title: "Henderson -- pre-inspection check", stage: "Booked Sat", color: "#3B82F6" },
        ],
      },
      CA: {
        title: "Today's call-outs",
        subtitle: "1 emergency, 2 booked",
        iconKey: "lightning",
        items: [
          { title: "Mr Davies -- battery jump", stage: "Emergency", color: "#EF4444" },
          { title: "Mrs Patel driveway -- brake job", stage: "On site", color: "#F97316" },
          { title: "Henderson -- pre-safety check", stage: "Booked Sat", color: "#3B82F6" },
        ],
      },
    },
    painHeros: [
      {
        region: "GB",
        problem:
          "Customer rings in the morning -- car won't start, needs me before work. By the time I've sorted parts and got there, half the morning's gone and I haven't quoted yet.",
        solution:
          "Quote from the van, take the deposit before you drive out, parts list ready in the next-job slot.",
        solutionPunchline: "The morning's yours, not the diary's.",
      },
      {
        region: "US",
        problem:
          "Customer calls in the morning -- car won't start, needs me before work. By the time I've sorted parts and got there, half the morning's gone and I haven't quoted yet.",
        solution:
          "Quote from the truck, take the deposit before you drive out, parts list ready in the next-job slot.",
        solutionPunchline: "The morning's yours, not the schedule's.",
      },
      {
        region: "CA",
        problem:
          "Customer calls in the morning -- car won't start, needs me before work. By the time I've sorted parts and got there, half the morning's gone and I haven't quoted yet.",
        solution:
          "Quote from the truck, take the deposit before you drive out, parts list ready in the next-job slot.",
        solutionPunchline: "The morning's yours, not the schedule's.",
      },
    ],
    painPoints: {
      GB: [
        "MOT-prep jobs you forgot to chase the customer on",
        "Parts ordered for the wrong car because the reg got mistyped",
        "Roadside callouts cratering a fully-booked day",
      ],
      US: [
        "Annual state inspections you forgot to chase the customer on",
        "Parts ordered for the wrong vehicle because the VIN got mistyped",
        "Roadside call-outs cratering a fully-booked day",
      ],
      CA: [
        "Provincial safety inspections you forgot to chase the customer on",
        "Parts ordered for the wrong vehicle because the VIN got mistyped",
        "Roadside call-outs cratering a fully-booked day",
      ],
    },
    benefits: {
      GB: [
        {
          title: "Vehicle records per customer",
          body: "Make + model + reg + last service mileage, all saved against the customer. Walk in knowing what you serviced last time.",
        },
        {
          title: "MOT-due reminders",
          body: "Tag a customer's MOT month and Manos pings you in the dashboard six weeks before. Book the prep work in early.",
        },
        {
          title: "Roadside call-out templates",
          body: "Battery, jump, alternator -- save the common emergencies as templates so you can quote and dispatch in 30 seconds.",
        },
        {
          title: "Parts and labour priced on-site",
          body: "Save quotes for the jobs you do most -- brake pads + discs, oil service, clutch. Three taps off the van.",
        },
      ],
      US: [
        {
          title: "Vehicle records per customer",
          body: "Make + model + plate + last service mileage, all saved against the customer. Walk in knowing what you serviced last time.",
        },
        {
          title: "Inspection-due reminders",
          body: "Tag a customer's annual inspection month and Manos pings you in the dashboard six weeks before. Book the prep work in early.",
        },
        {
          title: "Roadside call-out templates",
          body: "Battery, jump, alternator -- save the common emergencies as templates so you can quote and dispatch in 30 seconds.",
        },
        {
          title: "Parts and labor priced on-site",
          body: "Save quotes for the jobs you do most -- brake pads + rotors, oil service, transmission service. Three taps off the truck.",
        },
      ],
      CA: [
        {
          title: "Vehicle records per customer",
          body: "Make + model + plate + last service mileage, all saved against the customer. Walk in knowing what you serviced last time.",
        },
        {
          title: "Safety-inspection reminders",
          body: "Tag a customer's safety-inspection month and Manos pings you in the dashboard six weeks before. Book the prep work in early.",
        },
        {
          title: "Roadside call-out templates",
          body: "Battery, jump, alternator -- save the common emergencies as templates so you can quote and dispatch in 30 seconds.",
        },
        {
          title: "Parts and labour priced on-site",
          body: "Save quotes for the jobs you do most -- brake pads + rotors, oil service, transmission service. Three taps off the truck.",
        },
      ],
    },
    workflow: {
      GB: {
        title: "A typical day",
        steps: [
          "Morning: dashboard shows two driveway services booked + a roadside callout from overnight.",
          "On the way: voice-capture the new enquiry; the morning-of reminder will go out automatically.",
          "On-site: quote from a saved template, customer approves on their phone, fit the parts, mark complete.",
          "Evening: invoices sent, MOT-prep reminders pinged for next month's customers.",
        ],
      },
      US: {
        title: "A typical day",
        steps: [
          "Morning: dashboard shows two driveway services booked + a roadside call-out from overnight.",
          "On the way: voice-capture the new inquiry; the morning-of reminder will go out automatically.",
          "On-site: quote from a saved template, customer approves on their phone, install the parts, mark complete.",
          "Evening: invoices sent, inspection reminders pinged for next month's customers.",
        ],
      },
      CA: {
        title: "A typical day",
        steps: [
          "Morning: dashboard shows two driveway services booked + a roadside call-out from overnight.",
          "On the way: voice-capture the new inquiry; the morning-of reminder will go out automatically.",
          "On-site: quote from a saved template, customer approves on their phone, install the parts, mark complete.",
          "Evening: invoices sent, safety-inspection reminders pinged for next month's customers.",
        ],
      },
    },
  },
];

export const findTrade = (slug: string): TradeContent | undefined =>
  TRADES.find((t) => t.slug === slug);

/**
 * Trades whose customers are homeowners / landlords and whose work lands
 * inside a property record. These get the HouseProud integration callout
 * on their landing page. Non-property trades (hair stylists, tutors, dog
 * groomers, personal trainers) meet customers at a chair / studio / home
 * visit that doesn't belong to a property logbook and see the
 * BookingPageHero instead.
 *
 * Slugs match `TRADES[].slug` (plural form).
 */
export const PROPERTY_TRADE_SLUGS = new Set<string>([
  "plumbers",
  "electricians",
  "builders",
  "gas-engineers",
  "hvac-engineers",
  "painters",
  "locksmiths",
  "landscapers",
  "cleaners",
]);

export const isPropertyTrade = (slug: string): boolean =>
  PROPERTY_TRADE_SLUGS.has(slug);

/** Resolve a trade's pain hero for a given visitor region. Returns
 *  the first array entry matching the region, or the first entry
 *  overall if no match (so a GB-only trade still shows its GB pain
 *  to a Berlin visitor rather than disappearing). Returns undefined
 *  when the trade has no painHeros defined. */
export function resolvePainHero(
  trade: TradeContent,
  region: TradeRegion,
): TradePainHero | undefined {
  if (!trade.painHeros || trade.painHeros.length === 0) return undefined;
  return (
    trade.painHeros.find((p) => p.region === region) ?? trade.painHeros[0]
  );
}

/**
 * Resolve a region-keyed text field to the visitor's region with a GB
 * fallback. Used by the page loader to flatten the localised
 * TradeContent shape into ready-to-render values.
 *
 * Resolution order: requested region -> GB -> first available entry
 * in the map -> undefined (only when the field is empty, which is a
 * data bug; the route renders nothing for that section).
 */
export function resolveLocalized<T>(
  field: LocalizedTradeField<T> | undefined,
  region: TradeRegion,
  fallbackRegion: TradeRegion = "GB",
): T | undefined {
  if (!field) return undefined;
  const exact = field[region];
  if (exact !== undefined) return exact;
  const fallback = field[fallbackRegion];
  if (fallback !== undefined) return fallback;
  // Last-resort: return whatever entry exists. Order isn't guaranteed
  // but TS map iteration follows insertion, which is GB/US/CA on every
  // trade in this file.
  for (const value of Object.values(field)) {
    if (value !== undefined) return value as T;
  }
  return undefined;
}

/**
 * Resolve a trade's tap-to-pay ticket amount for a given visitor
 * region. Handles both the legacy single-number form (kept for
 * back-compat during the migration to per-region maps) and the
 * region-keyed map form.
 *
 * Falls back: requested region -> GB -> US -> CA -> null.
 */
export function resolveTicketAmount(
  trade: TradeContent,
  region: TradeRegion,
): number | null {
  const map = trade.tapToPayTicketAmount;
  if (map === undefined) return null;
  if (typeof map === "number") return map;
  return map[region] ?? map.GB ?? map.US ?? map.CA ?? null;
}

/**
 * Resolve a trade's display name for a given visitor region. Falls
 * back to `trade.name` when no per-region override is configured.
 * Used in the page heading and `<title>` tag to flip awkward UK trade
 * names ("Painter & decorator", "Gas engineer") to North American
 * equivalents without changing the slug.
 */
export function resolveTradeDisplayName(
  trade: TradeContent,
  region: TradeRegion,
): string {
  return resolveLocalized(trade.displayName, region) ?? trade.name;
}
